import { OrderedMap, List, Map } from "immutable";
import {
    stringComparator,
    dateComparator,
    numberComparator,
    customDateAndIdComparator,
    dateRangeFilter,
    numberRangeFilter,
    numberFilter,
    stringFilter
} from "utilities/table";
import moment from "moment";
import { StageBucketAction } from "components";
import { createEntityNameLink } from "../components/entity-name-link";
import { createBusinessProgramLink } from "../components/business-program-link";
import { formatCurrency, formatCurrencyWithLabels } from "utilities/currency";

import {
    TableSearchFilterDropdown,
    TableRangeFilterDropdown,
    TableSelectFilterDropdown,
    TableDateFilterDropdown
} from "components";
import { TABLE_DATA_TYPES } from "./table-data-types";
import CheckIcon from "components/check-icon";
import GetTag from "components/get-tag";
import { INPUT_TYPES } from "./index";
import _startCase from "lodash/startCase";
import _lowerCase from "lodash/lowerCase";
import { UNDERWRITING_JOB_TYPES, CASE_CATEGORY_TYPES, CASE_SOURCE, SCORE_CARD_RANGE, CIBIL_CONDITIONS, CIBIL_COMMENTS, BUSINESS_PRODUCT_TYPE } from "constants/enums";
import { ellipsisText } from "components/ellipsis-text";
import _noop from "lodash/noop";
import "styles/cibilTable.css";
import DocumentIcon from "../components/document-icon";
import { PPTableUpload } from "../components/pp-table-upload";
import generateLink from "../modules/companies/components/generate-link";



export const onCellGenerator = (handleSave, _) => record => ({
    record,
    editable: true,
    dataIndex: _.dataIndex,
    title: record.header || _.title,
    handleSave,
    rules: record.rules || _.rules,
    fieldType: record.fieldType || _.fieldType,
    filesMultiple: _.filesMultiple ? _.filesMultiple : false,
    options: record.options || _.options,
    getFileUploadParams: _.getFileUploadParams,
    uploadTarget: _.uploadTarget,
    viewable: _.viewable,
    max: record.max
});

export const CASE_ACTION_COL = {
    title: "Action",
    dataIndex: "actions",
    key: "actions",
    // width: 80,
    render: StageBucketAction
};

const onHeaderCellGenerator = ({ onClick, onResize, onResizeEnd }) => column => ({
    onClick: onClick ? onClick.bind(null, {
        dataIndex: column.dataIndex,
        dataType: column.dataType
    }) : _noop,
    onResize: onResize ? onResize.bind(null, column.dataIndex) : _noop,
    onResizeEnd: onResizeEnd ? onResizeEnd : _noop,
    width: column.width
});

export const BASE_INBOX_COLS = new OrderedMap({
    id: {
        title: "Case ID",
        dataIndex: "id",
        key: "id",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        permissionName: "id",
        render: (_, record) =>
            createEntityNameLink()(`PGCP-${record.id}`, record),
        width: 150,
        sorter: (a, b) => numberComparator(a.id, b.id)
    },
    stage: {
        title: "Stage",
        dataIndex: "stage",
        key: "stage",
        searchKey: "stage_name",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        permissionName: "stage",
        // render: (_, record) => `PGCP-${record.id}`,
        width: 150,
        sorter: (a, b) => stringComparator(a.stage, b.stage)
    },
    buyer_name: {
        title: "Company",
        dataIndex: "buyer_name",
        key: "buyer_name",
        searchKey: "company_name",
        permissionName: "buyer_name",
        width: 250,
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        // render: _ => _.name,
        render: (_, record) =>
            createEntityNameLink()(ellipsisText(_startCase(_lowerCase(_))), record),
        filterDropdown: TableSearchFilterDropdown,
        onFilter: stringFilter.bind(null, "buyer_name"),
        sorter: (a, b) => stringComparator(a.buyer_name, b.buyer_name)
    },
    proposed_limit: {
        title: "Proposed Limit",
        width: 150,
        key: "proposed_limit",
        dataIndex: "proposed_limit",
        dataType: TABLE_DATA_TYPES.NUMBER,
        onHeaderCellGenerator,
        render: _ => formatCurrency(+_, true),
        sorter: (a, b) => numberComparator(a.proposed_limit, b.proposed_limit)
    },
    seller_name: {
        title: "Seller",
        dataIndex: "seller_name",
        dataType: TABLE_DATA_TYPES.SELECT,
        mode: "tags",
        onHeaderCellGenerator,
        key: "seller_name",
        searchKey: "sellers_name",
        permissionName: "seller_name",
        width: 250,
        filterDropdown: TableSearchFilterDropdown,
        onFilter: stringFilter.bind(null, "seller_name"),
        render: (_, record) => ellipsisText(record.seller_name),
        sorter: (a, b) => stringComparator(a.seller_names, b.seller_names)
    },
    anchor_name: {
        title: "Anchor",
        dataIndex: "anchor_name",
        searchKey: "anchors_name",
        dataType: TABLE_DATA_TYPES.SELECT,
        mode: "tags",
        render: (_, record) => ellipsisText(record.anchor_name),
        onHeaderCellGenerator,
        key: "anchor_name",
        permissionName: "anchor_name",
        width: 250,
        filterDropdown: TableSearchFilterDropdown,
        onFilter: stringFilter.bind(null, "anchor_name"),
        sorter: (a, b) => stringComparator(a.anchor_name, b.anchor_name)
    },
    bank_comments_all_applicants: {
        title: "Bank Comments",
        dataIndex: "bank_comments_all_applicants",
        searchKey: "bank_comments_all_applicants",
        dataType: TABLE_DATA_TYPES.SELECT,
        mode: "tags",
        render: (_, record) => ellipsisText(_),
        onHeaderCellGenerator,
        key: "bank_comments_all_applicants",
        permissionName: "bank_comments_all_applicants",
        width: 250,
        filterDropdown: TableSearchFilterDropdown,
        onFilter: stringFilter.bind(null, "bank_comments_all_applicants"),
        sorter: (a, b) => stringComparator(a.bank_comments_all_applicants, b.bank_comments_all_applicants)
    },
    uuid: {
        title: "Case ID",
        dataIndex: "uuid",
        key: "uuid",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        permissionName: "uuid",
        render: (_, record) =>
            createEntityNameLink()(`PGCP-${record.id}`, record),
        width: 100,
        sorter: (a, b) => numberComparator(a.id, b.id)
    },
    case_type: {
        title: "Case Type",
        dataIndex: "case_type",
        key: "case_type",
        align: "center",
        dataType: TABLE_DATA_TYPES.ENUM,
        enums: CASE_CATEGORY_TYPES,
        render: _ => CASE_CATEGORY_TYPES.findKey(__ => __ === _),
        width: 150,
        sorter: (a, b) => stringComparator(a.case_type, b.case_type),
        onHeaderCellGenerator,
    },
    login_type: {
        title: "Login Type",
        dataIndex: "login_type",
        key: "login_type",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        align: "center",
        width: 150,
        sorter: (a, b) => stringComparator(a.login, b.login)
    },
    case_source: {
        title: "Case Source",
        dataIndex: "case_source",
        key: "case_source",
        align: "center",
        dataType: TABLE_DATA_TYPES.ENUM,
        enums: CASE_SOURCE,
        render: _ => CASE_SOURCE.findKey(__ => __ === _),
        width: 150,
        sorter: (a, b) => stringComparator(a.case_source, b.case_source),
        onHeaderCellGenerator,
    },
    created_by_details: {
        title: "Created By",
        dataIndex: "created_by_details",
        key: "created_by_details",
        searchKey: "created_by_name",
        align: "center",
        width: 250,
        dataType: TABLE_DATA_TYPES.STRING,
        render: _ => ellipsisText(_),
        sorter: (a, b) => stringComparator(a.created_by_details, b.created_by_details),
        onHeaderCellGenerator,
    },
    cibil_ready: {
        title: "Cibil Ready",
        dataIndex: "cibil_ready",
        key: "cibil_ready",
        align: "center",
        render: _ => _ ? "Yes" : "No",
        width: 100,
        sorter: (a, b) => stringComparator(a.cibil_ready, b.cibil_ready),
        onHeaderCellGenerator,
        dataType: TABLE_DATA_TYPES.BOOLEAN,
    },
    cibil_checked: {
        title: "Cibil Checked",
        dataIndex: "cibil_checked",
        key: "cibil_checked",
        align: "center",
        render: _ => _ ? "Yes" : "No",
        width: 100,
        sorter: (a, b) => stringComparator(a.cibil_checked, b.cibil_checked),
        dataType: TABLE_DATA_TYPES.BOOLEAN,
        onHeaderCellGenerator,
    },
    cibil_score: {
        title: "Cibil Score",
        dataIndex: "cibil_score",
        key: "cibil_score",
        align: "center",
        dataType: TABLE_DATA_TYPES.STRING,
        width: 150,
        sorter: (a, b) => stringComparator(a.cibil_score, b.cibil_score),
        onHeaderCellGenerator,
    },
    // reviewed_documents: {
    //     title: "Reviewed Documents",
    //     dataIndex: "reviewed_documents",
    //     key: "reviewed_documents",
    //     // searchKey: "reviewed_documents_count",
    //     align: "center",
    //     dataType: TABLE_DATA_TYPES.NUMBER,
    //     width: 150,
    //     sorter: (a, b) => numberComparator(a.reviewed_documents, b.reviewed_documents),
    //     onHeaderCellGenerator,
    // },
    // non_reviewed_documents: {
    //     title: "Non Reviewed Documents",
    //     dataIndex: "non_reviewed_documents",
    //     // searchKey: "non_reviewed_documents_count",
    //     key: "non_reviewed_documents",
    //     dataType: TABLE_DATA_TYPES.NUMBER,
    //     align: "center",
    //     width: 150,
    //     sorter: (a, b) => numberComparator(a.non_reviewed_documents, b.non_reviewed_documents),
    //     onHeaderCellGenerator,
    // },
    is_reassigned: {
        title: "New/Re-Submit",
        dataIndex: "is_reassigned",
        key: "is_reassigned",
        align: "center",
        dataType: TABLE_DATA_TYPES.SELECT,
        options: [
            {
                label: "New",
                value: "False"
            },
            {
                label: "Resubmitted",
                value: "True"
            }
        ],
        render: _ => _ ? "Resubmitted" : "New",
        width: 150,
        sorter: (a, b) => stringComparator(a.is_reassigned, b.is_reassigned),
        onHeaderCellGenerator,
    },
    assignment_date_value: {
        title: "Assigned/Submitted On",
        width: 200,
        key: "assignment_date_value",
        dataIndex: "assignment_date_value",
        // render: _ => _ && ,
        dataType: TABLE_DATA_TYPES.DATE,
        // onHeaderCellGenerator,
        sorter: (a, b) => dateComparator(a.assignment_date_value, b.assignment_date_value),
        defaultSortOrder: "descend",
        onHeaderCellGenerator,
    },
    assigned_user: {
        title: "Assigned To",
        key: "assigned_user",
        dataIndex: "assigned_user",
        searchKey: "assigned_user_name",
        width: 150,
        dataType: TABLE_DATA_TYPES.STRING,
        sorter: (a, b) => stringComparator(a.assigned_user, b.assigned_user),
        onHeaderCellGenerator,
    },
    address_line_1: {
        title: "Address",
        dataIndex: "address_line_1",
        key: "address_line_1",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        width: 300,
        filterDropdown: TableSearchFilterDropdown,
        onFilter: stringFilter.bind(null, "address_line_1"),
    },
    address_line_2: {
        title: "Address 2",
        dataIndex: "address_line_2",
        key: "address_line_2",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        width: 300,
        filterDropdown: TableSearchFilterDropdown,
        onFilter: stringFilter.bind(null, "address_line_2")
    },
    pincode: {
        title: "Pincode",
        dataIndex: "pincode",
        key: "pincode",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        width: 150,
        sorter: (a, b) => stringComparator(a.state, b.state)
    },
    city_name: {
        title: "City",
        dataIndex: "city_name",
        key: "city_name",
        width: 150,
        searchKey: "city_name",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        sorter: (a, b) => stringComparator(a.city, b.city)
    },
    district_name: {
        title: "District",
        dataIndex: "district_name",
        key: "district_name",
        searchKey: "district_name",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        width: 120,
        sorter: (a, b) => stringComparator(a.district, b.district)
    },
    state_name: {
        title: "State",
        dataIndex: "state_name",
        key: "state_name",
        searchKey: "state_name",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        width: 150,
        sorter: (a, b) => stringComparator(a.state, b.state)
    },
    sales_manager: {
        title: "Sales Manager",
        dataIndex: "sales_manager",
        key: "sales_manager",
        searchKey: "sales_manager_user_name",
        permissionName: "sales_manager",
        width: 200,
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        sorter: (a, b) => stringComparator(a.sales_manager_user, b.sales_manager_user)
    },
    "bank_name": {
        title: "Lending Partner",
        dataIndex: "bank_name",
        key: "bank_name",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        width: 200
    },
    current_user: {
        title: "Current User",
        dataIndex: "current_user",
        dataType: TABLE_DATA_TYPES.STRING,
        key: "current_user",
        onHeaderCellGenerator,
        width: 200
    },
    spoc_user: {
        title: "Key Contact",
        dataIndex: "spoc_user",
        key: "spoc_user",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        width: 200,
        render: _ => _.full_name,
        sorter: (a, b) => stringComparator(a.spoc_user, b.spoc_user)
    },

    supply_chain_score: {
        title: "Score",
        dataIndex: "supply_chain_score",
        key: "supply_chain_score",
        dataType: TABLE_DATA_TYPES.NUMBER,
        onHeaderCellGenerator,
        width: 80,
        sorter: (a, b) =>
            stringComparator(a.supply_chain_score, b.supply_chain_score)
    },
    progress_bar_cc: {
        title: "Progress",
        dataIndex: "progress_bar_cc",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        key: "progress_bar_cc",
        width: 100,
        sorter: (a, b) => stringComparator(a.progress_bar_cc, b.progress_bar_cc)
    },
    progress_bar_ps: {
        title: "Progress",
        dataIndex: "progress_bar_ps",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        key: "progress_bar_ps",
        width: 100,
        sorter: (a, b) => stringComparator(a.progress_bar_cc, b.progress_bar_cc)
    },
    checklist_progress: {
        title: "Checklist Completion",
        width: 100,
        key: "checklist_progress",
        dataIndex: "checklist_progress",
        permissionName: "checklist_progress",
        dataType: TABLE_DATA_TYPES.NUMBER,
        onHeaderCellGenerator,
        render: _ => `${_ || 0}%`,
        sorter: (a, b) => numberComparator(a.checklist_progress, b.checklist_progress)
    },
    supplychain_remarks: {
        title: "Supplychain Remarks",
        dataIndex: "supplychain_remarks",
        dataType: TABLE_DATA_TYPES.STRING,
        key: "supplychain_remarks",
        width: 150,
        onHeaderCellGenerator,
        render: (_) => ellipsisText(_),
        sorter: (a, b) => stringComparator(a.supplychain_remarks, b.supplychain_remarks)
    },
    system_recommendation: {
        title: "System Recommendation",
        dataIndex: "system_recommendation",
        key: "system_recommendation",
        width: 100,
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        filterDropdown: TableSelectFilterDropdown,
        onFilter: numberFilter.bind(null, "system_recommendation"),
        align: "center"
    },
    tat: {
        title: "TAT (Hrs)",
        dataIndex: "tat",
        key: "tat",
        width: 100,
        dataType: TABLE_DATA_TYPES.STRING,
        align: "center",
        onHeaderCellGenerator,
        filterDropdown: TableSearchFilterDropdown,
        onFilter: stringFilter.bind(null, "tat"),
        render: _ => _
    },
    industry_name: {
        title: "Industry Name",
        dataIndex: "industry_name",
        key: "industry_name",
        width: 200,
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        filterDropdown: TableSearchFilterDropdown,
        onFilter: stringFilter.bind(null, "industry_name"),
        render: _ => _
    },
    limit_basis_sales: {
        title: "Limit (Sales basis)",
        dataType: TABLE_DATA_TYPES.CURRENCY,
        onHeaderCellGenerator,
        dataIndex: "limit_basis_sales",
        key: "limit_basis_sales",
        width: 150,
        render: _ => (+_ ? formatCurrency(_, true) : "--"),
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "limit_basis_sales")
    },
    limit_basis_90_pctl: {
        title: "Limit (O/S base)",
        dataIndex: "limit_basis_90_pctl",
        dataType: TABLE_DATA_TYPES.CURRENCY,
        onHeaderCellGenerator,
        key: "limit_basis_90_pctl",
        width: 150,
        render: _ => (+_ ? formatCurrency(_, true) : "--"),
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "limit_basis_90_pctl")
    },
    date_of_appointment: {
        title: "Date of Appointment",
        dataIndex: "date_of_appointment",
        key: "date_of_appointment",
        dataType: TABLE_DATA_TYPES.DATE,
        onHeaderCellGenerator,
        width: 120,
        filterDropdown: TableDateFilterDropdown,
        onFilter: dateRangeFilter.bind(null, "date_of_appointment")
    },
    avg_monthly_no_sales: {
        title: "Avg. Monthly #Sales",
        dataIndex: "avg_monthly_no_sales",
        key: "avg_monthly_no_sales",
        dataType: TABLE_DATA_TYPES.NUMBER,
        onHeaderCellGenerator,
        width: 120,
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "avg_monthly_no_sales")
    },
    avg_monthly_no_repayments: {
        title: "Avg. Monthly #Repayments",
        dataIndex: "avg_monthly_no_repayments",
        dataType: TABLE_DATA_TYPES.NUMBER,
        onHeaderCellGenerator,
        key: "avg_monthly_no_repayments",
        width: 120,
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "avg_monthly_no_repayments")
    },
    no_bounces: {
        title: "Bounces #%",
        dataIndex: "no_bounces",
        dataType: TABLE_DATA_TYPES.NUMBER,
        onHeaderCellGenerator,
        key: "no_bounces",
        width: 100,
        render: (_, rec) =>
            +_ ? `${_}\n(${+rec.bounces_to_repayments_perc * 100}%)` : "--",
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "no_bounces")
    },

    repayment_period_mean: {
        title: "Avg. Repayment Period",
        dataIndex: "repayment_period_mean",
        dataType: TABLE_DATA_TYPES.NUMBER,
        onHeaderCellGenerator,
        key: "repayment_period_mean",
        width: 200,
        render: _ => (_ && +_ > -1 ? _ : "--"),
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "repayment_period_mean")
    },
    no_dpd_95_perc_amt_gt_0: {
        title: "DPD #%",
        dataIndex: "no_dpd_95_perc_amt_gt_0",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        key: "no_dpd_95_perc_amt_gt_0",
        width: 200,
        render: (_, rec) =>
            +_ ? `${_}\n(${+rec.no_dpd_95_perc_amt_gt_0_perc * 100}%)` : "--",
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "no_dpd_95_perc_amt_gt_0"),
    },
    // no_dpd_95_perc_amt_gt_0_perc: {
    //     title: "no_dpd_95_perc_amt_gt_0_perc",
    //     dataIndex: "no_dpd_95_perc_amt_gt_0_perc",
    //     key: "no_dpd_95_perc_amt_gt_0_perc",
    //     width: 100
    // },
    no_dpd_95_perc_amt_gt_30: {
        title: "DPD > 30 #%",
        dataIndex: "no_dpd_95_perc_amt_gt_30",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        key: "no_dpd_95_perc_amt_gt_30",
        width: 200,
        render: (_, rec) =>
            +_ ? `${_}\n(${+rec.no_dpd_95_perc_amt_gt_30_perc * 100}%)` : "--",
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "no_dpd_95_perc_amt_gt_30")
    },
    // no_dpd_95_perc_amt_gt_30_perc: {
    //     title: "no_dpd_95_perc_amt_gt_30_perc",
    //     dataIndex: "no_dpd_95_perc_amt_gt_30_perc",
    //     key: "no_dpd_95_perc_amt_gt_30_perc",
    //     width: 100
    // },
    no_dpd_95_perc_amt_gt_60: {
        title: "DPD > 60 #%",
        dataIndex: "no_dpd_95_perc_amt_gt_60",
        key: "no_dpd_95_perc_amt_gt_60",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        width: 200,
        render: (_, rec) =>
            +_ ? `${_}\n(${+rec.no_dpd_95_perc_amt_gt_60_perc * 100}%)` : "--",
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "no_dpd_95_perc_amt_gt_60")
    },
    // no_dpd_95_perc_amt_gt_60_perc: {
    //     title: "no_dpd_95_perc_amt_gt_60_perc",
    //     dataIndex: "no_dpd_95_perc_amt_gt_60_perc",
    //     key: "no_dpd_95_perc_amt_gt_60_perc",
    //     width: 100,
    //     render: (_, rec) => `${_}\n(${rec.no_dpd_95_perc_amt_gt_0_perc})`
    // },
    no_dpd_95_perc_amt_gt_90: {
        title: "DPD > 90 #%",
        dataIndex: "no_dpd_95_perc_amt_gt_90",
        key: "no_dpd_95_perc_amt_gt_90",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        width: 200,
        render: (_, rec) =>
            +_ ? `${_}\n(${+rec.no_dpd_95_perc_amt_gt_90_perc * 100}%)` : "--",
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "no_dpd_95_perc_amt_gt_90")
    },
    // no_dpd_95_perc_amt_gt_90_perc: {
    //     title: "no_dpd_95_perc_amt_gt_90_perc",
    //     dataIndex: "no_dpd_95_perc_amt_gt_90_perc",
    //     key: "no_dpd_95_perc_amt_gt_90_perc",
    //     width: 100
    // },
    retailer_share_of_the_total_sales_perc: {
        title: "% Share Sales",
        dataIndex: "retailer_share_of_the_total_sales_perc",
        key: "retailer_share_of_the_total_sales_perc",
        width: 200,
        dataType: TABLE_DATA_TYPES.NUMBER,
        onHeaderCellGenerator,
        render: _ => (+_ ? +_ : "--"),
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(
            null,
            "retailer_share_of_the_total_sales_perc"
        )
    },
    spoc_name: {
        title: "Spoc Name",
        dataIndex: "spoc_name",
        key: "spoc_name",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        permissionName: "spoc_name",
        width: 200
    },
    spoc_mobile: {
        title: "Spoc Mobile",
        dataIndex: "spoc_mobile",
        key: "spoc_mobile",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        permissionName: "spoc_mobile",
        width: 200
    },
    spoc_email: {
        title: "Spoc Email",
        dataIndex: "spoc_email",
        key: "spoc_email",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        permissionName: "spoc_email",
        width: 200
    },
    score: {
        title: "Score (1000)",
        dataIndex: "score",
        key: "score",
        dataType: TABLE_DATA_TYPES.NUMBER,
        onHeaderCellGenerator,
        permissionName: "retailer_share_of_the_total_sales_perc",
        width: 150,
        render: _ => (+_ ? _ : "--"),
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "score")
    },
    seller_count: {
        title: "Sellers Total",
        dataIndex: "seller_count",
        key: "seller_count",
        dataType: TABLE_DATA_TYPES.NUMBER,
        width: 100,
        onHeaderCellGenerator,
        sorter: (a, b) => numberComparator(a.seller_count, b.seller_count)
    },
    last_sync_date: {
        title: "Last Sync Date",
        dataIndex: "last_sync_date",
        key: "last_sync_date",
        dataType: TABLE_DATA_TYPES.DATE,
        width: 200,
        onHeaderCellGenerator,
        sorter: (a, b) => dateComparator(a.last_sync_date, b.last_sync_date)
    },
    sanction_date: {
        title: "Sanction Date",
        dataIndex: "sanction_date",
        key: "sanction_date",
        dataType: TABLE_DATA_TYPES.DATE,
        width: 200,
        onHeaderCellGenerator,
        sorter: (a, b) => dateComparator(a.sanction_date, b.sanction_date)
    },
    closing_balance: {
        title: "Closing Balance",
        dataIndex: "closing_balance",
        key: "closing_balance",
        render: _ => formatCurrency(_),
        dataType: TABLE_DATA_TYPES.CURRENCY,
        width: 200,
        onHeaderCellGenerator,
        sorter: (a, b) => numberComparator(a.closing_balance, b.closing_balance)
    },
    // post_sanction_kit_status: {
    //     title: "eSign Eligibility",
    //     dataIndex: "post_sanction_kit_status",
    //     render: (_, record) => `${record.ps_kit_eligible} (${_})`, 
    //     key: "post_sanction_kit_status",
    //     dataType: TABLE_DATA_TYPES.STRING,
    //     width: 150,
    //     onHeaderCellGenerator,
    //     // sorter: (a, b) => numberComparator(a.seller_count, b.seller_count)
    // },
    processing_fee_amount: {
        title: "Processing Fee Amount",
        dataIndex: "processing_fee_amount",
        key: "processing_fee_amount",
        dataType: TABLE_DATA_TYPES.CURRENCY,
        width: 100,
        render: _ => _ && formatCurrency(_, true),
    },
    is_seller_key_details_available: {
        title: "Seller Key Details Avalilable",
        dataIndex: "is_seller_key_details_available",
        key: "is_seller_key_details_available",
        dataType: TABLE_DATA_TYPES.BOOLEAN,
        width: 100,
        render: _ => _ ? "Yes" : "No"
    },
    // is_fee_available: {
    //     title: "Fee Available",
    //     dataIndex: "is_fee_available",
    //     key: "is_fee_available",
    //     dataType: TABLE_DATA_TYPES.BOOLEAN,
    //     width: 100,
    //     render: _ => _ ? "Yes" : "No"
    // },
    // is_scheme_created_on_lms: {
    //     title: "Scheme Create On LMS",
    //     dataIndex: "is_scheme_created_on_lms",
    //     key: "is_scheme_created_on_lms",
    //     dataType: TABLE_DATA_TYPES.BOOLEAN,
    //     width: 100,
    //     render: _ => _ ? "Yes" : "No"
    // },
    is_created_on_lms: {
        title: "Created on LMS",
        dataIndex: "is_created_on_lms",
        key: "is_created_on_lms",
        dataType: TABLE_DATA_TYPES.BOOLEAN,
        width: 100,
        render: (_) => _ ? "Yes" : "No"
    },
    // ps_kit_eligible: {
    //     title: "PS kit Eligible ?",
    //     dataIndex: "ps_kit_eligible",
    //     key: "ps_kit_eligible",
    //     dataType: TABLE_DATA_TYPES.STRING,
    //     render: _ => _ ? "Yes" : "No",
    //     width: 100,
    // },
    ps_kit_status: {
        title: "eSign Eligibility",
        dataIndex: "ps_kit_status",
        key: "ps_kit_status",
        dataType: TABLE_DATA_TYPES.STRING,
        render: (_, record) => _ !== "NA" ? `${record.ps_kit_eligible} (${_})` : record.ps_kit_eligible,
        width: 150,
    },
    status_welcome_call: {
        title: "Status",
        dataIndex: "status_welcome_call",
        key: "status_welcome_call",
        dataType: TABLE_DATA_TYPES.STRING,
        render: (_, record) => _ !== "NA" ? record.status_welcome_call ? `${record.status_welcome_call} (${_})` : "--" : record.status_welcome_call ? record.status_welcome_call : "--",
        width: 150,
    },
    sub_status_welcome_call: {
        title: "Sub-Status",
        dataIndex: "sub_status_welcome_call",
        key: "sub_status_welcome_call",
        dataType: TABLE_DATA_TYPES.STRING,
        render: (_, record) => _ !== "NA" ? record.sub_status_welcome_call ? `${record.sub_status_welcome_call} (${_})` : "--" : record.sub_status_welcome_call ? record.sub_status_welcome_call : "--",
        width: 150,
    },
    scheme_type_label: {
        title: "Scheme Type",
        dataIndex: "scheme_type_label",
        key: "scheme_type_label",
        dataType: TABLE_DATA_TYPES.STRING,
        width: 150,
    }
});

export const PAN_COL = {
    title: "PAN",
    key: "pan",
    dataIndex: "pan",
    editable: false,
    onCellGenerator
};

export const DATE_OF_BIRTH_COL = {
    title: "DOB",
    key: "dob",
    dataIndex: "dob",
    editable: false,
    onCellGenerator
};

export const ADDRESS_COL = {
    title: "Address",
    key: "address",
    dataIndex: "address",
    editable: false,
    onCellGenerator
};

export const PHONE_COL = {
    title: "Phone",
    key: "phone",
    dataIndex: "phone",
    editable: false,
    onCellGenerator
};

export const CIBIL_DETAILS = new OrderedMap({
    name: {
        title: "Name",
        key: "name",
        dataIndex: "name",
        width: 150,
    },
    bureau: {
        title: "Applicable Bureau",
        key: "bureau",
        dataIndex: "bureau",
        editable: true,
        fieldType: INPUT_TYPES.SELECT,
        options: [
            {
                label: "CIBIL",
                value: 1
            },
            {
                label: "CRIF",
                value: 2
            }
        ],
        width: 150,
        onCellGenerator
    },
    cibil_score: {
        title: "Credit Vision Score",
        key: "cibil_score",
        dataIndex: "cibil_score",
        editable: true,
        width: 150,
        onCellGenerator
    },
    personal_cibil_score: {
        title: "Personal Loan Score",
        key: "personal_cibil_score",
        dataIndex: "personal_cibil_score",
        editable: true,
        width: 150,
        onCellGenerator
    },
    is_new_to_credit: {
        title: "New to Credit",
        key: "is_new_to_credit",
        dataIndex: "is_new_to_credit",
        editable: true,
        width: 150,
        onCellGenerator,
        fieldType: INPUT_TYPES.SELECT,
        options: [{
            value: true,
            label: "Yes"
        }, {
            value: false,
            label: "No"
        }]
    },
    cibil_score_range: {
        title: "CIBIL Score Range",
        key: "cibil_score_range",
        dataIndex: "cibil_score_range",
        editable: true,
        width: 150,
        onCellGenerator,
        fieldType: INPUT_TYPES.SELECT,
        options: SCORE_CARD_RANGE.map((_, k) => ({
            label: k,
            value: _
        })).toList().toArray()
    },
    // is_new_to_credit: {
    //     title: "Is New to Credit?",
    //     key: "is_new_to_credit",
    //     dataIndex: "is_new_to_credit",
    //     width: 150,
    //     editable: true,
    //     onCellGenerator,
    //     fieldType: INPUT_TYPES.SELECT,
    //     options: [{
    //         label: "Yes",
    //         value: true
    //     }, {
    //         label: "No",
    //         value: false
    //     }]
    // },
    cibil_os: {
        title: "CIBIL Outstanding",
        key: "cibil_os",
        dataIndex: "cibil_os",
        editable: true,
        width: 150,
        onCellGenerator
    },
    crif_score: {
        title: "CRIF Score",
        key: "crif_score",
        dataIndex: "crif_score",
        editable: true,
        width: 150,
        onCellGenerator
    },

    cibil_remarks: {
        title: "Remarks",
        key: "cibil_remarks",
        dataIndex: "cibil_remarks",
        size: "large",
        fieldType: INPUT_TYPES.TEXTAREA,
        editable: true,
        width: 400,
        onCellGenerator
    },
    comments: {
        title: "Bank Comments",
        key: "comments",
        dataIndex: "comments",
        width: 150,
        render: _ => CIBIL_COMMENTS.findKey(__ => _ == __)
    },
    conditions: {
        title: "Bank Conditions",
        key: "conditions",
        dataIndex: "conditions",
        width: 150,
        render: _ => CIBIL_CONDITIONS.findKey(__ => _ == __)
    },
    descriptions: {
        title: "Bank Remarks",
        key: "descriptions",
        dataIndex: "descriptions",
        width: 200,
    },
    cibil_report: {
        title: "CIBIL Report ",
        key: "cibil_report",
        dataIndex: "cibil_report",
        fieldType: INPUT_TYPES.FILE,
        editable: true,
        viewable: true,
        width: 200,
        className: "cibil-col",
        getFileUploadParams: function () {
            return { company: this.record && this.record.company, document: 51 };
        },
        uploadTarget:
            `${process.env.BASE_URL}/company/documents/create/`,
        onCellGenerator
    },
    cibil_supported_report: {
        title: "Supporting Documents",
        key: "cibil_supported_report",
        dataIndex: "cibil_supported_report",
        fieldType: INPUT_TYPES.FILE,
        filesMultiple: true,
        editable: true,
        viewable: true,
        className: "cibil-col",
        width: 200,
        getFileUploadParams: function () {
            return { company: this.record && this.record.company, document: 52 };
        },
        uploadTarget:
            `${process.env.BASE_URL}/company/documents/create/`,
        onCellGenerator
    }
});

export const ASSESSMENT_COLS = new OrderedMap({
    label: {
        title: "Checkpoint",
        key: "label",
        dataIndex: "label",
        editable: false,
        render: _ => _ ? _ : "--",
        viewable: true
    },
    value: {
        title: "Value",
        key: "value",
        dataIndex: "value",
        render: _ => _
    },
    policy: {
        title: "Policy",
        key: "policy",
        dataIndex: "policy",
        editable: false,
        render: _ => _ ? _ : "--",
        viewable: true
    },
    review_category: {
        title: "Progcap Review",
        key: "review_category",
        dataIndex: "review_category",
        editable: false,
        viewable: true,
        fieldType: INPUT_TYPES.SELECT,
        // options: new Map({
        //     OK: "OK",
        //     Support: "Support",
        //     Conditional: "Conditional"
        // }).map((value, label) => ({
        //     label,
        //     value
        // })).toList().toArray(),
        // onCellGenerator,
        render: (_, record) => record.deviation === "Yes" ? record.deviation_reason : "OK"
    },
    remarks: {
        title: "Progcap Remarks",
        key: "remarks",
        dataIndex: "remarks",
        fieldType: INPUT_TYPES.TEXTAREA,
        editable: true,
        // onCellGenerator,
        // render: (_, record) => !record.deviation ? "--" : _
    },
    bank_review_category: {
        title: "Bank Review",
        key: "bank_review_category",
        dataIndex: "bank_review_category",
        editable: false,
        viewable: true,
        fieldType: INPUT_TYPES.SELECT,
        options: Map({
            L1: "L1",
            L2: "L2",
            L4: "L3"
        }).map((value, label) => ({
            label,
            value
        })).toList().toArray(),
        onCellGenerator
        // render: (_, record) => record.deviation === "Yes" ? record.deviation_reason : "OK"
    },
    bank_remarks: {
        title: "Bank Remarks",
        key: "bank_remarks",
        dataIndex: "bank_remarks",
        fieldType: INPUT_TYPES.TEXTAREA,
        editable: false,
        // onCellGenerator,
        // render: (_, record) => !record.deviation ? "--" : _
    },
});

export const ASSESSMENT_CAM_COLS = new OrderedMap({
    "bank.name": {
        title: "Name",
        key: "bank.name",
        dataIndex: "bank.name",
        render: _ => _ ? _ : "--",
    },
    cam_result: {
        title: "CAM Result",
        key: "cam_result",
        dataIndex: "cam_result",
        render: () => "OK"
    },
    score: {
        title: "Score of CAM",
        key: "score",
        dataIndex: "score",
        render: (_) => `${_}/100`
    },
    "action": {
        title: "View CAM",
        key: "action",
        dataIndex: "action"
    },
});

export const ASSESSMENT_CAM_DETAILS_COLS = new OrderedMap({
    label: {
        title: "Checkpoint",
        key: "label",
        dataIndex: "label",
        editable: false,
        render: _ => _ ? _ : "--",
        viewable: true
    },
    value: {
        title: "Value",
        key: "value",
        dataIndex: "value",
        render: _ => _
    },
    policy: {
        title: "Policy",
        key: "policy",
        dataIndex: "policy",
        editable: false,
        render: _ => _ ? _ : "--",
        viewable: true
    },
    review_category: {
        title: "Progcap Review",
        key: "review_category",
        dataIndex: "review_category",
        editable: false,
        viewable: true,
        fieldType: INPUT_TYPES.SELECT,
        // options: new Map({
        //     OK: "OK",
        //     Support: "Support",
        //     Conditional: "Conditional"
        // }).map((value, label) => ({
        //     label,
        //     value
        // })).toList().toArray(),
        // onCellGenerator,
        render: (_, record) => record.deviation === "Yes" ? record.deviation_reason : "OK"
    }
});

export const BANK_STATEMENT_COLS = new OrderedMap({
    txn_month_year: {
        title: "Months",
        key: "txn_month_year",
        dataIndex: "txn_month_year",
        editable: false,
        hide: false,
        render: _ => (_ ? _.format("MMM-YYYY") : _),
        width: 100
    },
    balance_5th: {
        title: "5th Balance",
        key: "balance_5th",
        dataIndex: "balance_5th",
        editable: true,
        hide: false,
        onCellGenerator
    },
    balance_10th: {
        title: "10th Balance",
        key: "balance_10th",
        dataIndex: "balance_10th",
        editable: true,
        hide: true,
        onCellGenerator
    },
    balance_15th: {
        title: "15th Balance",
        key: "balance_15th",
        dataIndex: "balance_15th",
        editable: true,
        hide: false,
        onCellGenerator
    },
    balance_20th: {
        title: "20th Balance",
        key: "balnce.balance_20th",
        dataIndex: "balnce.balance_20th",
        hide: true,
        editable: true,
        onCellGenerator
    },
    balance_25th: {
        title: "25th Balance",
        key: "balance_25th",
        dataIndex: "balance_25th",
        hide: false,
        editable: true,
        onCellGenerator
    },
    balance_30th: {
        title: "30th Balance",
        key: "balance_30th",
        dataIndex: "balance_30th",
        hide: true,
        editable: true,
        onCellGenerator
    },
    total_inward_cheque: {
        title: "No of Inward Cheques",
        key: "total_inward_cheque",
        dataIndex: "total_inward_cheque",
        editable: true,
        hide: false,
        onCellGenerator
    },
    total_outward_cheque: {
        title: "No of Outward Cheques",
        key: "total_outward_cheque",
        dataIndex: "total_outward_cheque",
        editable: true,
        hide: false,
        onCellGenerator
    },
    total_inward_cheque_bounce: {
        title: "Inward Cheque Bounce",
        key: "total_inward_cheque_bounce",
        dataIndex: "total_inward_cheque_bounce",
        editable: true,
        hide: false,
        onCellGenerator
    },
    total_outward_cheque_bounce: {
        title: "Outward Cheque Bounce",
        key: "total_outward_cheque_bounce",
        dataIndex: "total_outward_cheque_bounce",
        editable: true,
        hide: false,
        onCellGenerator
    }
});

export const ADDRESS_TABLE_COLS = new OrderedMap({
    associate: {
        title: "Associate",
        dataIndex: "associate",
        key: "associate"
    },
    address_type: {
        title: "Address Type",
        dataIndex: "address_type",
        key: "address_type"
    },
    ownership: {
        title: "Ownership Status",
        dataIndex: "ownnership",
        key: "ownership"
    },
    residing_date: {
        title: "Residing since",
        dataIndex: "residing_date",
        key: "residing_date"
    },
    address_line_1: {
        title: "Address",
        dataIndex: "address_line_1",
        key: "address_line_1",
        width: 300
    },
    address_line_2: {
        title: "Address",
        dataIndex: "address_line_2",
        key: "address_line_2",
        width: 100
    },
    city_name: {
        title: "City",
        dataIndex: "city_name",
        key: "city_name",
        width: 120
    },
    district_name: {
        title: "District",
        dataIndex: "district_name",
        key: "district_name",
        width: 120
    },
    state_name: {
        title: "State",
        dataIndex: "state_name",
        key: "state_name",
        width: 120
    },
    is_communication_address: {
        title: "Is Communication Address",
        dataIndex: "is_communication_address",
        key: "is_communication_address",
        width: 120,
        render: _ => (_ ? GetTag("Yes") : null)
    },
    is_pv_done: {
        title: "Personal Verification",
        dataIndex: "is_pv_done",
        key: "is_pv_done",
        width: 120,
        render: CheckIcon
    },
    action: {
        title: "Action",
        dataIndex: "action",
        key: "action",
        width: 80
    }
});

class InboxCasesColsGenerator {
    constructor(selectedCols) {
        this.updateSelectedCols(selectedCols);
    }
    updateSelectedCols = (selectedCols = new List()) => {
        if (selectedCols && selectedCols.size) {
            this.obj = BASE_INBOX_COLS.filter((_, key) =>
                selectedCols.includes(key)
            );
            this.cols = selectedCols;
        } else {
            this.obj = BASE_INBOX_COLS;
            this.cols = BASE_INBOX_COLS.map((_, k) => k).toList();
        }
        this.colsWidth = this.obj.toList().reduce((agg, _) => agg + _.width, 0);
        return this;
    };
    getCols() {
        return this.obj;
    }
    getAllCols() {
        return BASE_INBOX_COLS;
    }
    getColsWidth() {
        return this.colsWidth;
    }
}

export const USERS_LIST_COLS = new OrderedMap({
    name: {
        title: "Name",
        dataIndex: "name",
        align: "center",
        key: "name"
    },
    email: {
        title: "Email-Id",
        dataIndex: "email",
        align: "center",
        key: "email"
    },
    group: {
        title: "Associated Group",
        dataIndex: "group",
        align: "center",
        key: "group"
    }
});

export const GROUPS_LIST_COLS = new OrderedMap({
    name: {
        title: "Name",
        dataIndex: "name",
        align: "center",
        key: "name"
    },
    action: {
        title: "Permissions",
        dataIndex: "action",
        key: "action",
        width: 80
    }
});

export const BrandsTableCols = new OrderedMap({
    product_name: {
        title: "Channel Product Label",
        dataIndex: "name",
        key: "channel_product_name",
        align: "center",
        editable: false,
        onCellGenerator
    },
    value: {
        title: "Value",
        dataIndex: "value",
        key: "value",
        editable: true,
        onCellGenerator,
        render: _ => _.formatCurrency(+_, true),
        align: "center"
    },
    from_period: {
        title: "From Period",
        dataIndex: "from_period",
        editable: true,
        key: "from_period",
        onCellGenerator,
        align: "center",
        render: _ => (_ ? moment(_).format("DD/MM/YY") : "--")
    },
    to_period: {
        title: "To Period",
        dataIndex: "to_period",
        key: "to_period",
        onCellGenerator,
        align: "center",
        editable: true
    }
});

export const INBOX_CASES_COLS = new InboxCasesColsGenerator(new List([]));

export const UNDERWRITING_REQUEST_COLS = new OrderedMap({
    requestId: {
        title: "Request Id",
        dataIndex: "request_id",
        key: "request_id"
    },
    triggerTime: {
        title: "Trigger Time",
        dataIndex: "triggered_at",
        key: "triggered_at",
        render: _ => moment(_).format("DD-MM-YYYY hh:mm:ss a")
    },
    distributorName: {
        title: "Distributor",
        dataIndex: "distributor_name",
        key: "distributor_name"
    },
    tallyCompanyCode: {
        title: "Tally Code",
        dataIndex: "tally_company_code",
        key: "tally_company_code"
    },
    tenor: {
        title: "Tenor",
        dataIndex: "tenor",
        key: "tenor"
    },
    minimum_vintage: {
        title: "Minimum Vintage(months)",
        dataIndex: "minimum_vintage",
        key: "minimum_vintage"
    },
    minimum_limit: {
        title: "Minimum Limit",
        dataIndex: "minimum_limit",
        key: "minimum_limit"
    },

    current_status: {
        title: "Status",
        dataIndex: "current_status",
        key: "current_status",
        render: _ => UNDERWRITING_JOB_TYPES.findKey(__ => __ === _)
    }
    // remarks: {
    //     title: "Remarks",
    //     dataIndex: "remarks",
    //     key: "remarks"
    // }
});

export const LEDGER_ANALYSIS_COLS = new OrderedMap({
    date_appointment: {
        title: "Appointment",
        dataIndex: "date_appointment",
        key: "date_appointment",
        align: "center",
        width: 100
    },
    ledger_name: {
        title: "Ledger",
        dataIndex: "ledger_name",
        key: "ledger_name",
        align: "center",
        width: 300
    },
    limit: {
        title: "Limit",
        children: new OrderedMap({
            limit_basis_sales: {
                title: "basis sales",
                key: "limit_basis_sales",
                dataIndex: "limit_basis_sales",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            },
            limit_basis_os: {
                title: "basis O/S",
                key: "limit_basis_os",
                dataIndex: "limit_basis_os",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            }
        })
            .toList()
            .toArray(),
        width: 300
    },
    last_12_months: {
        title: "Value (Last 12 months)",
        children: new OrderedMap({
            sales_amount_last_12_months: {
                title: "Sales",
                key: "sales_amount_last_12_months",
                dataIndex: "sales_amount_last_12_months",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            },
            sales_return_amount_last_12_months: {
                title: "Sales Return",
                key: "sales_return_amount_last_12_months",
                dataIndex: "sales_return_amount_last_12_months",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            },
            net_sales_amount_last_12_months: {
                title: "Net Sales",
                key: "net_sales_amount_last_12_months",
                dataIndex: "net_sales_amount_last_12_months",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            }
        })
            .toList()
            .toArray(),
        width: 450
    },
    avg_monthly_no: {
        title: "Average Monthly Transactions",
        children: [
            {
                title: "Sales",
                key: "avg_monthly_no_sales",
                dataIndex: "avg_monthly_no_sales",
                align: "center",
                width: 150
            },
            {
                title: "Repayments",
                key: "avg_monthly_no_repayments",
                dataIndex: "avg_monthly_no_repayments",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            }
        ],
        width: 300
    },
    overdues: {
        title: "Overdues",
        children: [
            {
                title: "(# | %)",
                key: "overdues_0",
                dataIndex: "overdues_0",
                align: "center",
                width: 150
            },
            {
                title: "> 30 days (# | %)",
                key: "overdues_30",
                dataIndex: "overdues_30",
                align: "center",
                width: 150
            },
            {
                title: "> 60 days (# | %)",
                key: "overdues_60",
                dataIndex: "overdues_60",
                align: "center",
                width: 150
            },
            {
                title: "> 90 days (# | %)",
                key: "overdues_90",
                dataIndex: "overdues_90",
                align: "center",
                width: 150
            }
        ],
        width: 600
    },
    bounces: {
        title: "Bounces(# | %)",
        key: "bounces",
        dataIndex: "bounces",
        align: "center",
        width: 150
    },
    oustanding: {
        title: "Outstanding",
        children: [
            {
                title: "Mean",
                key: "mean_os",
                dataIndex: "mean_os",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            },
            {
                title: "Median",
                key: "median_os",
                dataIndex: "median_os",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            },
            {
                title: "25th %ile",
                key: "os_bal_25_pctl",
                dataIndex: "os_bal_25_pctl",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            },
            {
                title: "75th %ile",
                key: "os_bal_75_pctl",
                dataIndex: "os_bal_75_pctl",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            },

            {
                title: "90th %ile",
                key: "os_bal_90_pctl",
                dataIndex: "os_bal_90_pctl",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            }
        ],
        width: 750
    },
    avg_repayment_period: {
        title: "Average Repayment Period",
        key: "avg_repayment_period",
        dataIndex: "avg_repayment_period",
        align: "center",
        width: 150
    },
    oldest_outstanding_invoice: {
        title: "Oldest Outstanding Invoice",
        children: new OrderedMap({
            oldest_invoice_os_date: {
                title: "Date",
                key: "oldest_invoice_os_date",
                dataIndex: "oldest_invoice_os_date",
                align: "center",
                width: 150
            },
            oldest_invoice_os_ageing: {
                title: "Ageing",
                key: "oldest_invoice_os_ageing",
                dataIndex: "oldest_invoice_os_ageing",
                align: "center",
                width: 150
            },
            oldest_invoice_os_amount: {
                title: "Amount",
                key: "oldest_invoice_os_amount",
                dataIndex: "oldest_invoice_os_amount",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            },
            oldest_invoice_os_unpaid_amount: {
                title: "Remaining Amount",
                key: "oldest_invoice_os_unpaid_amount",
                dataIndex: "oldest_invoice_os_unpaid_amount",
                align: "center",
                render: _ => formatCurrency(_, true),
                width: 150
            }
        })
            .toList()
            .toArray(),
        width: 600
    }

    // no_active_months_in_last_12_months: {
    //     title: "Active in Last 12 months",
    //     key: "no_active_months_in_last_12_months",
    //     dataIndex: "no_active_months_in_last_12_months",
    //     align: "center"
    // },
    // average_monthly_value_of_sales: {
    //     title: "Avg. Monthly Sales Value",
    //     key: "average_monthly_value_of_sales",
    //     dataIndex: "average_monthly_value_of_sales",
    //     align: "center"
    // }
});

export const MPA_LIST_TABLE_COLS = new OrderedMap({
    company: {
        title: "Company",
        key: "company",
        dataIndex: "company.name",
        // align: "center",
        // render: _ => formatCurrency(_, true),
        render: (_, record) =>
            createBusinessProgramLink()(_startCase(_lowerCase(_)), record),
        width: 150,
        align: "left"
    },
    program_type: {
        title: "Program Type",
        key: "program_type",
        dataIndex: "program_type",
        // align: "center",
        width: 150,
        align: "left"
    },
    program_size: {
        title: "Program Size",
        key: "program_size",
        dataIndex: "program_size",
        // align: "center",
        render: _ => formatCurrencyWithLabels(_),
        width: 150,
        align: "left"
    },

    product_lines: {
        title: "Product Line",
        key: "product_lines",
        dataIndex: "product_lines",
        // align: "center",
        // render: _ => formatCurrency(_, true),
        width: 150,
        align: "left"
    }
    // steps_completed: {
    //     title: "Steps Completed",
    //     key: "steps_completed",
    //     align: "center",
    //     render: (_, record) =>
    //         `${Object.keys(record).filter(_ => _.includes("is_")).filter(_ => record[_] === true).length} / ${Object.keys(record).filter(_ => _.includes("is_")).length}`
    // },
    // action: {
    //     title: "Action",
    //     key: "action",
    //     dataIndex: "action",
    //     align: "center",
    //     width: 150
    // }
});

export const DOCUMENT_CHECKLIST_COLS = new OrderedMap({
    entity: {
        title: "Entity",
        key: "entity",
        dataIndex: "entity",
        align: "center"
    },
    product: {
        title: "Product",
        key: "product",
        dataIndex: "product",
        align: "center"
    },
    type: {
        title: "Type",
        key: "type",
        dataIndex: "type",
        align: "center"
    },
    categories: {
        title: "Categories",
        key: "categories",
        dataIndex: "categories",
        align: "center"
    },
    action: {
        title: "Action",
        key: "action",
        dataIndex: "action",
        align: "center",
        width: 150
    }
});

export const CASE_SEARCH_CONST_COLS = new OrderedMap({
    company: {
        title: "Company",
        key: "company",
        dataIndex: "company",
        align: "center",
        render: (_, record) => {
            return createEntityNameLink()(_startCase(_lowerCase(_.name)), record, true);
        }
        // render: _ => _ && _startCase(_lowerCase(_.name))
    },
    "current_case_task[0].this_stage": {
        title: "Current Stage",
        key: "current_case_task[0].this_stage",
        dataIndex: "current_case_task[0].this_stage",
        align: "center",
        render: (_, record) =>
            record && record.current_case_task[0] ? record.current_case_task[0].this_stage.stage_name : null
    },
    "current_case_task[0].this_user": {
        title: "Current User",
        key: "current_case_task[0].this_user",
        dataIndex: "current_case_task[0].this_user",
        align: "center",
        render: (_, record) =>
            record && record.current_case_task[0] && record.current_case_task[0].this_user ? record.current_case_task[0].this_user.full_name : null
    },
    proposed_limit: {
        title: "Proposed Limit",
        key: "proposed_limit",
        align: "center",
        render: _ => formatCurrency(+_.proposed_limit, true)
    },
    "current_case_task[0].start_date": {
        title: "Ageing",
        key: "current_case_task[0].start_date",
        dataIndex: "current_case_task[0].start_date",
        align: "center",
        render: (_, record) =>
            record && record.current_case_task[0]
                ? moment().diff(
                    moment(record.current_case_task[0].start_date),
                    "days"
                ) + "days"
                : null
    },
    case_status: {
        title: "Case Status",
        key: "case_status",
        dataIndex: "case_status",
        align: "center"
    },
    sanction_date: {
        title: "Sanction Date",
        key: "sanction_date",
        dataIndex: "sanction_date",
        align: "center",
        render: _ => _ ? moment(_).format("DD/MM/YYYY") : "--"
    },
    sanction_limit: {
        title: "Sanction Limit",
        key: "sanction_limit",
        dataIndex: "sanction_limit",
        render: _ => _ ? formatCurrency(_, true) : "--"
    }
});

export const REGION_SPOC_COLS = new OrderedMap({
    spoc_name: {
        title: "Spoc Name",
        key: "spoc_name",
        dataIndex: "spoc_name",
        align: "left"

    },
    spoc_phone: {
        title: "Spoc Phone No",
        key: "spoc_phone",
        dataIndex: "spoc_phone",
        align: "left"
    },
    spoc_email: {
        title: "Spoc Email",
        key: "spoc_email",
        dataIndex: "spoc_email",
        align: "left"
    },

    relationship_manager: {
        title: "Relationship Manager",
        key: "relationship_manager",
        dataIndex: "relationship_manager",
        align: "left"
    },
});

export const DISTRIBUTOR_LIST_COLS = new OrderedMap({
    company: {
        title: "Company",
        key: "company",
        dataIndex: "company",
        align: "left"
    },
    gstin: {
        title: "GSTIN",
        key: "gstin",
        dataIndex: "gstin",
        align: "left"
    },
    spoc_name: {
        title: "Spoc Name",
        key: "spoc_name",
        dataIndex: "spoc_name",
        align: "left"
    },
    spoc_phone: {
        title: "Spoc Phone",
        key: "spoc_phone",
        dataIndex: "spoc_phone",
        align: "left"
    },
    spoc_email: {
        title: "Spoc Phone",
        key: "spoc_email",
        dataIndex: "spoc_email",
        align: "left"
    },
    address_line_1: {
        title: "Address Line 1",
        key: "address_line_1",
        dataIndex: "address_line_1",
        align: "left"
    },
    address_line_2: {
        title: "Address Line 2",
        key: "address_line_2",
        dataIndex: "address_line_2",
        align: "left"
    },
    city: {
        title: "City",
        key: "city",
        dataIndex: "city",
        align: "left"
    },
    state: {
        title: "State",
        key: "state",
        dataIndex: "state",
        align: "left"
    },
});

export const SUPPLY_CHAIN_COLS = new OrderedMap({
    seller: {
        title: "Seller",
        key: "seller",
        dataIndex: "seller",
        align: "left",
        render: _ => _ && _.name,
        width: 200
    },
    anchor: {
        title: "Anchor",
        key: "anchor",
        dataIndex: "anchor",
        align: "left",
        render: _ => _ && _.name,
        width: 200
    },
    // seller_erp: {
    //     title: "Seller ERP",
    //     key: "seller_erp",
    //     dataIndex: "seller_erp",
    //     align: "left"
    // },
    // ledger_name: {
    //     title: "Ledger Name",
    //     key: "Ledger Name",
    //     dataIndex: "ledger_name",
    //     align: "center"
    // },
    // acceptable: {
    //     title: "Acceptable",
    //     key: "acceptable",
    //     dataIndex: "acceptable",
    //     align: "left"
    // }
    // scheme: {
    //     title: "Scheme",
    //     key: "scheme",
    //     dataIndex: "scheme",
    //     align: "left",
    //     editable: true,
    //     width: 420,
    // },
    scheme_description: {
        title: "Base Description",
        key: "scheme_description",
        dataIndex: "scheme_description",
        align: "left",
        width: 300,
        render: _ => _ || "Not Mapped"
    },
    dwd_sp_description: {
        title: "Flexible Parameters",
        key: "dwd_sp_description",
        dataIndex: "dwd_sp_description",
        align: "left",
        width: 150,
        render: _ => _ || "Not Mapped"
    },
    updated_by_user: {
        title: "Scheme Mapped By",
        key: "updated_by_user",
        dataIndex: "updated_by_user",
        render: _ => _ && _.email,
        align: "center",
        width: 150
    },
    processing_fee_amount: {
        title: "Processing Fee",
        key: "processing_fee_amount",
        dataIndex: "processing_fee_amount",
        align: "center",
        render: _ => _ && formatCurrency(_, false),
        width: 150
    },
    sub_limit: {
        title: "Sub-limit",
        key: "sub_limit",
        dataIndex: "sub_limit",
        align: "center",
        render: _ => _ && formatCurrency(_, false),
        width: 150
    },
});


export const ACCOUNT_STATEMENT_COLS = new OrderedMap({
    // temp_id: {
    //     title: "ID",
    //     dataIndex: "temp_id",
    //     dataType: TABLE_DATA_TYPES.STRING,
    //     key: "temp_id",
    //     filterDropdown: TableRangeFilterDropdown,
    //     // 
    //     defaultSortOrder: "ascend",
    //     sorter: (a, b) => numberComparator(a.id, b.id)
    // },
    op_date: {
        title: "Date",
        dataIndex: "op_date",
        key: "op_date",
        dataType: TABLE_DATA_TYPES.DATE,
        onHeaderCellGenerator,
        render: _ => (_ ? moment(_).format("DD/MM/YYYY") : "--"),
        sorter: (a, b, type) =>
            customDateAndIdComparator(a, b, type, "op_date"),
        // filterIcon: FilterIcon,
        filterDropdown: TableDateFilterDropdown,
        onFilter: dateRangeFilter.bind(null, "op_date")
    },
    description: {
        title: "Description",
        dataIndex: "description",
        key: "description",
        dataType: TABLE_DATA_TYPES.STRING,
        onHeaderCellGenerator,
        sorter: (a, b) => numberComparator(a.description, b.description),
        // filterIcon: FilterIcon,
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "description")
    },
    invoice_date: {
        title: "Invoice Date",
        dataIndex: "invoice_date",
        key: "invoice_date",
        // defaultSortOrder: "ascend",
        dataType: TABLE_DATA_TYPES.DATE,
        onHeaderCellGenerator,
        render: _ => (_ ? moment(_).format("DD/MM/YYYY") : "--"),
        // filterIcon: FilterIcon,
        filterDropdown: TableDateFilterDropdown,
        onFilter: dateRangeFilter.bind(null, "invoice_date")
    },
    due_date: {
        title: "Due Date",
        dataIndex: "due_date",
        key: "due_date",
        // defaultSortOrder: "ascend",
        dataType: TABLE_DATA_TYPES.DATE,
        onHeaderCellGenerator,
        render: _ => (_ ? moment(_).format("DD/MM/YYYY") : "--"),
        // filterIcon: FilterIcon,
        filterDropdown: TableDateFilterDropdown,
        onFilter: dateRangeFilter.bind(null, "due_date")
    },
    repaid_date: {
        title: "Repaid Date",
        dataIndex: "repaid_date",
        key: "repaid_date",
        // defaultSortOrder: "ascend",
        dataType: TABLE_DATA_TYPES.DATE,
        onHeaderCellGenerator,
        render: _ => (_ ? moment(_).format("DD/MM/YYYY") : "--"),
        // filterIcon: FilterIcon,
        filterDropdown: TableDateFilterDropdown,
        onFilter: dateRangeFilter.bind(null, "repaid_date")
    },
    debit: {
        title: "Debit",
        dataIndex: "debit",
        key: "debit",
        dataType: TABLE_DATA_TYPES.DATE,
        onHeaderCellGenerator,
        render: _ => formatCurrency(_),
        sorter: (a, b) => numberComparator(a.debit, b.debit),
        // filterIcon: FilterIcon,
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "debit")
    },
    credit: {
        title: "Credit",
        dataIndex: "credit",
        key: "credit",
        dataType: TABLE_DATA_TYPES.CURRENCY,
        onHeaderCellGenerator,
        render: _ => formatCurrency(_),
        // sorter: (a, b) => numberComparator(a.credit, b.credit),
        // filterIcon: FilterIcon,
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "credit")
    },
    outstanding: {
        title: "Outstanding",
        dataIndex: "outstanding",
        key: "outstanding",
        dataType: TABLE_DATA_TYPES.CURRENCY,
        onHeaderCellGenerator,
        render: _ => formatCurrency(_),
        sorter: (a, b) => numberComparator(a.credit, b.credit),
        // filterIcon: FilterIcon,
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "outstanding")
    }
});

export const REPAYMENT_INVOICE_COLS = new OrderedMap({
    seller_name: {
        title: "Seller",
        dataIndex: "seller_name",
        key: "seller_name",
        width: 100,
        render: _ => `#${_}`,
        sorter: (a, b) => stringComparator(a.seller_name, b.seller_name),
        filterDropdown: TableSearchFilterDropdown,
        onFilter: stringFilter.bind(null, "seller_name"),
        align: "right"
    },
    invoice_no: {
        title: "Invoice No",
        dataIndex: "invoice_no",
        key: "invoice_no",
        width: 100,
        render: _ => `#${_}`,
        sorter: (a, b) => stringComparator(a.invoice_no, b.invoice_no),
        filterDropdown: TableSearchFilterDropdown,
        onFilter: stringFilter.bind(null, "invoice_no"),
        align: "left"
    },
    principal_outstanding: {
        title: "O/S Amount",
        dataIndex: "principal_outstanding",
        key: "principal_outstanding",
        width: 150,
        render: _ => formatCurrency(_),
        sorter: (a, b) => numberComparator(a.principal_outstanding, b.principal_outstanding),
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "principal_outstanding"),
        align: "left",
        dataType: TABLE_DATA_TYPES.CURRENCY,
        onHeaderCellGenerator
    },
    cashback_as_of_today: {
        title: "Cashback",
        dataIndex: "cashback_as_of_today",
        key: "cashback_as_of_today",
        width: 150,
        render: _ => formatCurrency(_),
        sorter: (a, b) => numberComparator(a.cashback_as_of_today, b.cashback_as_of_today),
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "cashback_as_of_today"),
        align: "left",
        dataType: TABLE_DATA_TYPES.CURRENCY,
        onHeaderCellGenerator
    },
    interest_save_as_of_today: {
        title: "Interest",
        dataIndex: "interest_save_as_of_today",
        key: "interest_save_as_of_today",
        width: 150,
        render: _ => formatCurrency(_),
        sorter: (a, b) => numberComparator(a.interest_save_as_of_today, b.interest_save_as_of_today),
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "interest_save_as_of_today"),
        align: "left",
        dataType: TABLE_DATA_TYPES.CURRENCY,
        onHeaderCellGenerator
    },
    invoice_date: {
        title: "Invoice Date",
        dataIndex: "invoice_date",
        key: "invoice_date",
        width: 150,
        sorter: (a, b) => dateComparator(a.invoice_date, b.invoice_date),
        filterDropdown: TableRangeFilterDropdown,
        onFilter: dateRangeFilter.bind(null, "invoice_date"),
        align: "left",
        dataType: TABLE_DATA_TYPES.DATE,
        onHeaderCellGenerator
    },
    invoice_amount: {
        title: "Invoice Amount",
        dataIndex: "invoice_amount",
        key: "invoice_amount",
        width: 150,
        render: _ => formatCurrency(_),
        sorter: (a, b) => numberComparator(a.invoice_amount, b.invoice_amount),
        filterDropdown: TableRangeFilterDropdown,
        onFilter: numberRangeFilter.bind(null, "invoice_amount"),
        align: "left",
        dataType: TABLE_DATA_TYPES.CURRENCY,
        onHeaderCellGenerator
    },
    date_till_last_cashback_45: {
        title: "Last Cashback Date",
        dataIndex: "date_till_last_cashback_45",
        key: "date_till_last_cashback_45",
        width: 150,
        sorter: (a, b) => dateComparator(a.date_till_last_cashback_45, b.date_till_last_cashback_45),
        filterDropdown: TableRangeFilterDropdown,
        onFilter: dateRangeFilter.bind(null, "date_till_last_cashback_45"),
        align: "left",
        dataType: TABLE_DATA_TYPES.DATE,
        onHeaderCellGenerator
    },
    soft_due_date: {
        title: "Soft Due Date",
        dataIndex: "soft_due_date",
        key: "soft_due_date",
        width: 150,
        sorter: (a, b) => dateComparator(a.soft_due_date, b.soft_due_date),
        filterDropdown: TableRangeFilterDropdown,
        onFilter: dateRangeFilter.bind(null, "soft_due_date"),
        align: "left",
        dataType: TABLE_DATA_TYPES.DATE,
        onHeaderCellGenerator
    },
});

export const PREVIOUSLY_CREATED_CASE_COLS = new OrderedMap({
    id: {
        title: "Case ID",
        dataIndex: "id",
        key: "id",
        render: (_, record) =>
            createEntityNameLink()(`PGCP-${record.id}`, record),
    },
    company_name: {
        title: "Company",
        dataIndex: "company",
        key: "company",
        render: (_, record) =>
            _ && createEntityNameLink()(_startCase(_lowerCase(_.name)), record),
    },
    seller_name: {
        title: "Seller",
        dataIndex: "seller_name",
        key: "seller_name",
        render: (_, record) =>
            record.supply_chain &&
            record.supply_chain.length &&
            record.supply_chain.map(_ => _.seller && _.seller.name).join(", ")
    },
    anchor_name: {
        title: "Anchor",
        dataIndex: "anchor_name",
        key: "anchor_name",
        render: (_, record) =>
            record.supply_chain &&
            record.supply_chain.length &&
            record.supply_chain.map(_ => _.anchor && _.anchor.name).join(", ")
    },
    bank: {
        title: "Lending Partner",
        dataIndex: "bank",
        key: "bank",
        render: _ => _ && _.short_name
    },
    sales_manager_user: {
        title: "Sales Manager",
        dataIndex: "sales_manager_user",
        key: "sales_manager_user",
        render: _ => _ && _.full_name
    },
    proposed_limit: {
        title: "Proposed Limit",
        dataIndex: "proposed_limit",
        key: "proposed_limit",
        render: _ => _ && formatCurrency(_, true)
    },
    this_stage: {
        title: "Current Stage",
        dataIndex: "current_case_task[0]",
        key: "this_stage",
        render: _ => _ && _.this_stage && _.this_stage.stage_name
    },
    this_user: {
        title: "Current User",
        dataIndex: "current_case_task[0]",
        key: "this_user",
        render: _ => _ && _.this_user && _.this_user.full_name
    }
});

export const UNDERWRITING_DISTRIBUTOR_COLS = new OrderedMap({
    distributor_name: {
        title: "Distriutor Name",
        dataIndex: "distributor_name",
        key: "distributor_name"
    },
    total_ledgers: {
        title: "Total Ledgers",
        dataIndex: "total_ledgers",
        key: "total_ledgers"
    },
    total_retailers: {
        title: "Retailers",
        dataIndex: "total_retailers",
        key: "total_retailers"
    },
    total_sales: {
        title: "Total Sales",
        dataIndex: "total_sales",
        key: "total_sales",
        render: _ => formatCurrency(_, true)
    },
    eligible_retailers: {
        title: "Eligible Retailers",
        dataIndex: "id__count",
        key: "id__count"
    },
    net_sales_amount_last_12_months__sum: {
        title: "Eligible Retailers Net Sales Last 12 Months",
        dataIndex: "net_sales_amount_last_12_months__sum",
        key: "net_sales_amount_last_12_months__sum",
        render: _ => formatCurrency(_, true)
    },
    limit_on_tenor__sum: {
        title: "Limit Basis Tenor",
        dataIndex: "limit_on_tenor__sum",
        key: "limit_on_tenor__sum",
        render: _ => formatCurrency(_, true)
    },
    // sales_coverage: {
    //     title: "Sales Coverage",
    //     dataIndex: "limit_basis_sales__sum",
    //     key: "limit_basis_sales__sum"
    // }
});

export const ENTITY_VERIFICATION_COLS = new OrderedMap({
    entity: {
        title: "Entity",
        key: "entity_name",
        dataIndex: "entity_name",
        render: _ => _ || "--"
    },
    pan: {
        title: "PAN",
        key: "pan_no",
        dataIndex: "pan_no",
        render: _ => _ || "--"
    },
    is_company_pan_verified: {
        title: "Verified",
        key: "is_company_pan_verified",
        dataIndex: "is_company_pan_verified"
    }
});

export const APPLICANT_VERIFICATION_COLS = new OrderedMap({
    associate: {
        title: "Applicant",
        key: "associate",
        dataIndex: "associate",
        render: _ => _ || "--"
    },
    phone_no: {
        title: "Phone Number",
        key: "phone_no",
        dataIndex: "phone_no",
        render: _ => _ || "--"
    },
    is_phone_number_verified: {
        title: "Verified",
        key: "is_phone_number_verified",
        dataIndex: "is_phone_number_verified"
    },
    email_id: {
        title: "Email",
        key: "email_id",
        dataIndex: "email_id",
        render: _ => _ || "--"
    },
    is_email_verified: {
        title: "Verified",
        key: "is_email_verified",
        dataIndex: "is_email_verified",
    },
    aadhar_no: {
        title: "Aadhaar",
        key: "aadhar_no",
        dataIndex: "aadhar_no",
        render: _ => _ || "--"
    },
    is_aadhaar_number_verified: {
        title: "Verified",
        key: "is_aadhaar_number_verified",
        dataIndex: "is_aadhaar_number_verified",
    },
    pan_no: {
        title: "PAN",
        key: "pan_no",
        dataIndex: "pan_no",
        render: _ => _ || "--"
    },
    is_pan_number_verified: {
        title: "Verified",
        key: "is_pan_number_verified",
        dataIndex: "is_pan_number_verified"
    }
});

export const CIBIL_EDITABLE_COLS = new OrderedMap({
    case: {
        title: "Case ID",
        key: "case",
        dataIndex: "case",
        width: 150,
        render: _ => `PGCP-${_}`
    },
    "associate_data.company_associate": {
        title: "Name",
        key: "associate_data.company_associate",
        dataIndex: "associate_data.company_associate",
        width: 150,
        render: (_) => _ && `${_.first_name} ${_.last_name}`
    },
    "associate_data.kyc_docs.pan": {
        title: "PAN",
        key: "associate_data.kyc_docs.pan",
        dataIndex: "associate_data.kyc_docs.pan",
        editable: false,
        width: 100,
    },
    "associate_data.kyc_docs.aadhar": {
        title: "Aadhaar",
        key: "associate_data.kyc_docs.aadhar",
        dataIndex: "associate_data.kyc_docs.aadhar",
        editable: false,
        width: 100,
    },
    "associate_data.cibil_score": {
        title: "Credit Vision Score",
        key: "associate_data.cibil_score",
        dataIndex: "associate_data.cibil_score",
        editable: true,
        width: 150,
        onCellGenerator
    },
    "associate_data.personal_cibil_score": {
        title: "Personal Loan Score",
        key: "associate_data.personal_cibil_score",
        dataIndex: "associate_data.personal_cibil_score",
        editable: true,
        width: 150,
        onCellGenerator
    },
    "associate_data.cibil_score_range": {
        title: "CIBIL Score Range",
        key: "associate_data.cibil_score_range",
        dataIndex: "associate_data.cibil_score_range",
        editable: true,
        width: 150,
        onCellGenerator,
        fieldType: INPUT_TYPES.SELECT,
        options: SCORE_CARD_RANGE.map((_, k) => ({
            label: k,
            value: _
        })).toList().toArray()
    },
    "associate_data.crif_score": {
        title: "CRIF Score",
        key: "associate_data.crif_score",
        dataIndex: "associate_data.crif_score",
        editable: true,
        width: 150,
        onCellGenerator
    },
    "associate_data.cibil_os": {
        title: "CIBIL Outstanding",
        key: "associate_data.cibil_os",
        dataIndex: "associate_data.cibil_os",
        editable: true,
        width: 150,
        onCellGenerator
    },
});

export const CIBIL_READ_ONLY_COLS = new OrderedMap({
    case: {
        title: "Case ID",
        key: "case",
        dataIndex: "case",
        width: 150,
        render: _ => `PGCP-${_}`,
        sorter: (a, b) => numberComparator(a.id, b.id)
    },
    case_stage: {
        title: "Current Stage",
        key: "case_stage",
        dataIndex: "case_stage",
        width: 150,
        render: _ => _ && _.length && _.join(", ")
    },

    "associate_data.company_associate": {
        title: "Name",
        key: "associate_data.company_associate",
        dataIndex: "associate_data.company_associate",
        width: 150,
        render: (_) => _ && `${_.first_name} ${_.last_name}`,
        sorter: (a, b) => stringComparator(a.first_name, b.first_name)
    },
    date_of_submission: {
        title: "Date of Submission",
        key: "date_of_submission",
        dataIndex: "date_of_submission",
        editable: false,
        width: 150,
        sorter: (a, b) => dateComparator(a.assignment_date_value, b.assignment_date_value),
    },
    proposed_limit: {
        title: "Proposed Limit",
        key: "proposed_limit",
        dataIndex: "proposed_limit",
        width: 150,
        render: _ => formatCurrency(_),
        sorter: (a, b) => numberComparator(a.proposed_limit, b.proposed_limit)
    },
    "associate_data.cibil_score": {
        title: "Credit Vision Score",
        key: "associate_data.cibil_score",
        dataIndex: "associate_data.cibil_score",
        width: 150,
        // sorter: (a, b) => numberComparator(a.associate_data && a.associate_data.cibil_score, b.associate_data && b.associate_data.cibil_score)
    },
    "associate_data.personal_cibil_score": {
        title: "Personal Loan Score",
        key: "associate_data.personal_cibil_score",
        dataIndex: "associate_data.personal_cibil_score",
        width: 150,
    },
    "associate_data.cibil_score_range": {
        title: "CIBIL Score Range",
        key: "associate_data.cibil_score_range",
        dataIndex: "associate_data.cibil_score_range",
        width: 150,
        render: __ => SCORE_CARD_RANGE.findKey(_ => _ == __)
    },
    "associate_data.crif_score": {
        title: "CRIF Score",
        key: "associate_data.crif_score",
        dataIndex: "associate_data.crif_score",
        width: 150,
    },
    "associate_data.cibil_os": {
        title: "CIBIL Outstanding",
        key: "associate_data.cibil_os",
        dataIndex: "associate_data.cibil_os",
        width: 150,
    },
    "associate_data.credit_check.comments": {
        title: "Bank Comments",
        key: "associate_data.credit_check.comments",
        dataIndex: "associate_data.credit_check.comments",
        width: 150,
        render: _ => CIBIL_COMMENTS.findKey(__ => _ == __),
        sorter: (a, b) => numberComparator(
            a.associate_data && a.associate_data.credit_check && a.associate_data.credit_check.comments,
            b.associate_data && b.associate_data.credit_check && b.associate_data.credit_check.comments
        )
    },
    "associate_data.credit_check.conditions": {
        title: "Conditions",
        key: "associate_data.credit_check.conditions",
        dataIndex: "associate_data.credit_check.conditions",
        width: 150,
        render: _ => CIBIL_CONDITIONS.findKey(__ => _ == __),
        sorter: (a, b) => numberComparator(
            a.associate_data && a.associate_data.credit_check && a.associate_data.credit_check.conditions,
            b.associate_data && b.associate_data.credit_check && b.associate_data.credit_check.conditions
        )

    },
    "associate_data.credit_check.descriptions": {
        title: "Description",
        key: "associate_data.credit_check.descriptions",
        dataIndex: "associate_data.credit_check.descriptions",
        width: 250,
    },
});


export const PAYMENT_PROGRAM_COLS = new OrderedMap({
    "case.id": {
        title: "Case ID",
        // key: "case.id",
        // dataIndex: "case.id",
        // width: 150,
        // render: (_, record) =>
        //     createEntityNameLink()(`PGCP-${record.case.id}`, record.case, false, false, true),
    },
    "case.company.name": {
        title: "Companies(Payee)",
        key: "case.company.name",
        dataIndex: "case.company.name",
        width: 150,
    },
    payer: {
        title: "Payer",
        key: "payer",
        dataIndex: "payer",
        width: 150
    },
    "case.seller": {
        title: "Seller",
        key: "case.seller",
        dataIndex: "case.seller",
        width: 150
    },
    "case.anchor": {
        title: "Anchor",
        key: "case.anchor",
        dataIndex: "case.anchor",
        width: 150
    },
    assignment_date: {
        title: "Initiated Date",
        key: "assignment_date",
        dataIndex: "assignment_date",
        width: 150
    }
});

export const ENTITY_DETAILS_COLUMNS = (handleDocumentUpdate, showUploader) => new OrderedMap({
    "gst_certificate_1": {
        title: "GST Certificate 1",
        key: "gst_certificate_1",
        type: "upload",
        render: (item, list) => {
            if (!showUploader) return DocumentIcon(item?.gst_certificate_1?.documents[0]?.status || "", item?.gst_certificate_1?.documents || "");
            return PPTableUpload(
                item.gst_certificate_1,
                item?.gst_certificate_1 && DocumentIcon(item?.gst_certificate_1?.documents[0]?.status || "", item?.gst_certificate_1?.documents || ""),
                "upload",
                "delete",
                handleDocumentUpdate,
                list
            );
        },
        width: 150
    },
    "gst_certificate_2": {
        title: "GST Certificate 2",
        key: "gst_certificate_2",
        render: (item, list) => {
            if (!showUploader) return DocumentIcon(item?.gst_certificate_2?.documents[0]?.status, item?.gst_certificate_2?.documents || "");
            return PPTableUpload(
                item.gst_certificate_2,
                item?.gst_certificate_2 ? DocumentIcon(item?.gst_certificate_2?.documents[0]?.status, item?.gst_certificate_2?.documents || "") : "",
                "upload",
                "delete",
                handleDocumentUpdate,
                list
            );
        },
        width: 150
    },
    "gst_certificate_3": {
        title: "GST Certificate 3",
        key: "gst_certificate_3",
        render: (item, list) => {
            if (!showUploader) return DocumentIcon(item?.gst_certificate_3?.documents[0]?.status, item?.gst_certificate_3?.documents || "");
            return PPTableUpload(
                item.gst_certificate_3,
                item?.gst_certificate_3 ? DocumentIcon(item?.gst_certificate_3?.documents[0]?.status, item.gst_certificate_3.documents || "") : "",
                "upload",
                "delete",
                handleDocumentUpdate,
                list
            );
        },
        width: 150
    },
    "company_pan": {
        title: "Company Pan",
        key: "company_pan",
        render: (item, list) => {
            if (!showUploader) return DocumentIcon(item?.company_pan?.documents[0]?.status, item?.company_pan?.documents || "");
            return PPTableUpload(
                item.company_pan,
                item?.company_pan ? DocumentIcon(item?.company_pan?.documents[0]?.status, item?.company_pan?.documents || "") : "",
                "upload",
                "delete",
                handleDocumentUpdate,
                list
            );
        },
        width: 150
    },
    "cheque_photo": {
        title: "Cheque Photo",
        key: "cheque_photo",
        render: (item, list) => {
            if (!showUploader) return DocumentIcon(item?.cheque_photo?.documents[0]?.status, item?.cheque_photo?.documents || "");
            return PPTableUpload(
                item.cheque_photo,
                item?.cheque_photo ? DocumentIcon(item?.cheque_photo?.documents[0]?.status, item?.cheque_photo?.documents || "") : "",
                "upload",
                "delete",
                handleDocumentUpdate,
                list
            );
        },
        width: 150
    },
});

export const APPLICANTS_DETAILS_COLUMNS = (handleDocumentUpdate, showUploader) => new OrderedMap({
    "name": {
        title: "Name",
        key: "name.data",
        dataIndex: "name.data",
        width: 150

    },
    "email": {
        title: "Email",
        key: "email.data",
        dataIndex: "email.data",
        width: 200
    },
    "mobile_number": {
        title: "Mobile",
        key: "mobile_number.data",
        dataIndex: "mobile_number.data",
        width: 150
    },
    "pan": {
        title: "PAN",
        key: "pan",
        render: (item, list) => {
            if (!showUploader) return DocumentIcon(item.pan?.documents[0]?.status || "", item.pan?.documents || "");
            return PPTableUpload(
                item.pan,
                item.pan && DocumentIcon(item.pan?.documents[0]?.status || "", item.pan?.documents || ""),
                "upload",
                "delete",
                handleDocumentUpdate,
                list
            );
        },
        width: 150
    },
    "aadhar_front": {
        title: "Aadhar Front",
        key: "aadhar_front",
        render: (item, list) => {
            if (!showUploader) return DocumentIcon(item?.aadhar_front?.documents[0]?.status || "", item?.aadhar_front?.documents || "");
            return PPTableUpload(
                item.aadhar_front,
                item.aadhar_front && DocumentIcon(item?.aadhar_front?.documents[0]?.status || "", item?.aadhar_front?.documents || ""),
                "upload",
                "delete",
                handleDocumentUpdate,
                list
            );
        },
        width: 150

    },
    "aadhar_back": {
        title: "Aadhar Back",
        key: "aadhar_back",
        render: (item, list) => {
            if (!showUploader) return DocumentIcon(item?.aadhar_back?.documents[0]?.status || "", item?.aadhar_back?.documents || "");
            return PPTableUpload(
                item.aadhar_back,
                item.aadhar_back && DocumentIcon(item?.aadhar_back?.documents[0]?.status || "", item?.aadhar_back?.documents || ""),
                "upload",
                "delete",
                handleDocumentUpdate,
                list
            );
        },
        width: 150

    },
});

// buyer_code , seller_code, onboarding_id, buyer_name

export const PAYPRO_PENDING_APPROVAL_COLS = new OrderedMap({
    "onboarding_id": {
        title: "Onboarding Id",
        key: "onboarding_id",
        dataIndex: "onboarding_id",
        width: 150,
        render: (_, record) =>
            createEntityNameLink()(`AD-${record.onboarding_id}`, record, false, false, true),
        onFilter: stringFilter.bind(null, "onboarding_id"),
        dataType: TABLE_DATA_TYPES.STRING,
        is_filter_applicable: true
    },
    "dealer_code": {
        title: "Dealer Code",
        key: "dealer_code",
        dataIndex: "dealer_code",
        width: 150,
        dataType: TABLE_DATA_TYPES.STRING,
        is_filter_applicable: true
    },
    "submitted_at": {
        title: "Submitted At",
        key: "submitted_at",
        dataIndex: "submitted_at",
        width: 150,
        render: _ => (_ ? moment(_).format("DD/MM/YY") : "--")
    },
    "main_dealer_code": {
        title: "Main Dealer Code",
        key: "main_dealer_code",
        dataIndex: "main_dealer_code",
        width: 150,
        dataType: TABLE_DATA_TYPES.STRING,
    },
    "dealer_name": {
        title: "Buyer Name",
        key: "dealer_name",
        dataIndex: "dealer_name",
        width: 150,
        is_filter_applicable: true,
        dataType: TABLE_DATA_TYPES.STRING,
    },
    "applicant_name": {
        title: "Applicant Name",
        key: "applicant_name",
        dataIndex: "applicant_name",
        width: 150,
        dataType: TABLE_DATA_TYPES.STRING,
        is_filter_applicable: true
    },
    "applicant_contact": {
        title: "Contact No",
        key: "applicant_contact",
        dataIndex: "applicant_contact",
        width: 150,
    },
    "applicant_email": {
        title: "Email",
        key: "applicant_email",
        dataIndex: "applicant_email",
        width: 150
    },
    "user": {
        title: "Assigned User",
        key: "open_task_this_user.name",
        dataIndex: "open_task_this_user.name",
        width: 200
    },
    "split": {
        title: "Action",
        dataIndex: "split",
        key: "split",
        width: 200
    }
    // registered_date: {
    // title: "Registered Date",
    // key: "registered_date",
    // dataIndex: "registered_date",
    // dataType: TABLE_DATA_TYPES.DATE,
    // width: 150,
    // render: _ => (_ ? moment(_).format("DD/MM/YY") : "--")
    // }
});

export const AGREEMENT_APPLICANT_DETAILS_COLS = new OrderedMap({
    name: {
        title: "Name",
        dataIndex: "name",
        key: "name",
        render: _ => (_ ? _ : "--")
    },
    email: {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: 200,
        render: _ => (_ ? _ : "--")
    },
    phone: {
        title: "Phone",
        dataIndex: "phone",
        key: "phone",
        width: 150,
        render: _ => (_ ? _ : "--")
    },
    sign_url: {
        title: "Url",
        key: "sign_url",
        dataIndex: "sign_url",
        width: 150,
        render: _ => (_ ? _ : "--")
    },
    "copy": {
        title: "Copy",
        key: "copy",
        dataIndex: "copy",
    },
    status: {
        title: "Status",
        key: "status",
        dataIndex: "status",
        render: _ => (_ ? _ : "--")
    },
    expiry_date: {
        title: "URL expiry date",
        key: "expiry_date",
        dataIndex: "expiry_date",
        render: _ => (_ ? moment(_).format("MMM Do YYYY, h:mm a") : "--")
    },
    initiation_date: {
        title: "Initiation date",
        key: "initiation_date",
        dataIndex: "initiation_date",
        render: _ => (_ ? moment(_).format("MMM Do YYYY, h:mm a") : "--")
    },
    signed_date: {
        title: "Signed date",
        key: "signed_date",
        dataIndex: "signed_date",
        render: _ => (_ ? moment(_).format("MMM Do YYYY, h:mm a") : "--")
    },
    "action": {
        title: "Action",
        key: "action",
    },
    "is_manual_signed": {
        title: "Mark as Manually Signed",
        key: "is_manual_signed",
        dataIndex: "is_manual_signed",
    },
});

export const PAYPRO_CLOSED_COLS = new OrderedMap({
    "case.id": {
        title: "Case ID",
        // key: "case.id",
        // dataIndex: "case.id",
        // width: 150,
        // render: (_, record) =>
        //     createEntityNameLink()(`PGCP-${record.case.id}`, record.case, false, false, true),
    },
    sign_url: {
        title: "Url",
        key: "sign_url",
        dataIndex: "sign_url",
        width: 150,
        render: _ => (_ ? _ : "--")
    },
    "copy": {
        title: "Copy",
        key: "copy",
        dataIndex: "copy",
    },
    status: {
        title: "Status",
        key: "status",
        dataIndex: "status",
        render: _ => (_ ? _ : "--")
    },
    expiry_date: {
        title: "URL expiry date",
        key: "expiry_date",
        dataIndex: "expiry_date",
        render: _ => (_ ? moment(_).format("MMM Do YYYY, h:mm a ") : "--")
    },
    "action": {
        title: "Action",
        key: "action",
    },
});

export const AGREEMENT_LOGS_COLS = new OrderedMap({
    modified_by: {
        title: "Modified By",
        dataIndex: "modified_by",
        key: "modified_by",
        render: _ => (_ ? _ : "--")
    },
    status: {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: _ => (_ ? _ : "--")
    },
    modified_date: {
        title: "Modified Date",
        dataIndex: "modified_date",
        key: "modified_date",
        render: _ => (_ ? moment(_).format("MMM Do YYYY") : "--")
    },
});

export const DB_ONBOARDING_COLS =  new OrderedMap({
    "action_detail_id": {
        title: "Case Id",
        key: "action_detail_id",
        dataIndex: "action_detail_id",
        width: 150,
        render: (_, record) => generateLink(`/app/companies/${record.company_id}/?source=db-onboarding&stage=${record.current_stage_id}&action_detail_id=${_}&action_task_id=${record.action_task_id}`, _),
        onFilter: stringFilter.bind(null, "onboarding_id"),
        dataType: TABLE_DATA_TYPES.STRING,
        is_filter_applicable: true
    },
    "company_gstin": {
        title: "GSTIN",
        key: "company_gstin",
        dataIndex: "company_gstin",
        width: 150,
        dataType: TABLE_DATA_TYPES.STRING,
        is_filter_applicable: true
    },
    "company_name": {
        title: "Company Name",
        key: "company_name",
        dataIndex: "company_name",
        width: 150,
        dataType: TABLE_DATA_TYPES.STRING,
        is_filter_applicable: true
    },
    "state": {
        title: "State",
        key: "state",
        dataIndex: "state",
        width: 150,
    },
    "user": {
        title: "Assigned User",
        key: "user",
        dataIndex: "assigned_user.name",
        render : _ => _ || '--',
        width: 200
    },
    "d2r_db_limit": {
        title: "D2R DB Limit",
        key: "d2r_db_limit",
        dataIndex: "d2r_db_limit",
        render : _ => _ || '--',
        width: 200
    },
    "split": {
        title: "Action",
        dataIndex: "split",
        key: "split",
        width: 200
    }
});

export const ONBOARDING_CASES_LIST_COLS = (onCaseClick) =>  new OrderedMap({
    "id": {
        title: "Case Id",
        key: "id",
        dataIndex: "id",
        width: 150,
        render: (_, record) => onCaseClick(record),
        onFilter: stringFilter.bind(null, "onboarding_id"),
        dataType: TABLE_DATA_TYPES.STRING,
        is_filter_applicable: true
    },
    "company": {
        title: "Business Name",
        key: "company",
        dataIndex: "company.name",
        width: 150,
        dataType: TABLE_DATA_TYPES.STRING,
        is_filter_applicable: true
    },
    "company_gstin": {
        title: "GSTIN",
        key: "company_gstin",
        dataIndex: "company.gstin",
        width: 150,
        dataType: TABLE_DATA_TYPES.STRING,
        is_filter_applicable: true
    },

    "industry": {
        title: "Primary Industry",
        key: "industry",
        render : (_ , record) => record.getPrimaryIndustry(),
        width: 150,
        dataType: TABLE_DATA_TYPES.STRING,
        is_filter_applicable: true
    },
    "current_action_task": {
        title: "Case Owner",
        key: "current_action_task",
        dataIndex: "current_action_task.this_user.full_name",
        render : _ => _ || '--',
        width: 200
    },
    "proposed_program_size_in_cr": {
        title: "Program Size in (cr)",
        key: "proposed_program_size_in_cr",
        dataIndex: "proposed_program_size_in_cr",
        width: 150,
        dataType: TABLE_DATA_TYPES.STRING,
        render : _ => _,
    },
    "product_type":{
        title: "Business Product Name",
        key: "product_type",
        dataIndex: "product_type",
        width: 150,
        render : (_, record) => record.getProductNameDBLIST() || "--",
        dataType: TABLE_DATA_TYPES.STRING,

    },
    "split": {
        title: "Action",
        dataIndex: "split",
        key: "split",
        width: 200
    }
});


export const EMI_SCHEDULE_COLUMNS = new OrderedMap({
    instalment_nbr: {
        title: "EMI",
        dataIndex: "instalment_nbr",
        key: "instalment_nbr",
        width: 80,
        align: "center"
    },
    emi_amount: {
        title: "EMI Amount",
        dataIndex: "emi_amount",
        key: "emi_amount",
        width: 120,
        render: _ => formatCurrency(_, false),
        align: "center",
    },
    principal_amount: {
        title: "Principal Amount",
        dataIndex: "principal_amount",
        key: "principal_amount",
        width: 120,
        align: "center",
    },
    interest_amount: {
        title: "Interest Amount",
        dataIndex: "interest_amount",
        key: "interest_amount",
        width: 120,
        render: _ => formatCurrency(_, false),
        align: "center"
    },
    principal_receivable: {
        title: "Outstanding Loan",
        dataIndex: "principal_receivable",
        key: "principal_receivable",
        width: 120,
        render: _ => formatCurrency(_, false),
        align: "center"
    },
    interest_paid_till_date: {
        title: "Cumulative Interest Amount",
        dataIndex: "interest_paid_till_date",
        key: "interest_paid_till_date",
        render: _ => formatCurrency(_, false),
        width: 180,
        align: "center"
    },
    tentative_due_date: {
        title: "Tentative Due Date",
        dataIndex: "tentative_due_date",
        key: "tentative_due_date",
        render: _ => moment(_).format("DD MMM YY"),
        width: 150,
        align: "center"
    },
});


export const D2R_LIMIT_COLUMNS = new OrderedMap({
    limit_approved: {
        title: "D2R Limit Approved Under DB",
        dataIndex: "limit_approved",
        key: "d2r_limit_approved_db",
        width: 80,
        render: _ => formatCurrency(_, false),
        align: "center"
    },
    limit_utilized: {
        title: "Limit Utilized",
        dataIndex: "limit_utilized",
        key: "d2r_utilized_limit",
        width: 120,
        render: _ => formatCurrency(_, false),
        align: "center",
    },
    limit_available: {
        title: "Limit Available",
        dataIndex: "limit_available",
        key: "d2r_limit_available",
        width: 120,
        render: _ => formatCurrency(_, false),
        align: "center",
    }
});
export const COMPANY_ASSESSMENT_COLS = new OrderedMap({
    label: {
        title: "Checkpoint",
        key: "label",
        dataIndex: "label",
        editable: false,
        render: _ => _ ? _ : "--",
        viewable: true
    },
    value: {
        title: "Value",
        key: "value",
        dataIndex: "value",
        render: _ => _
    },
    policy: {
        title: "Policy",
        key: "policy",
        dataIndex: "policy",
        editable: false,
        render: (_ , record) => _ ? _ : record.title ? "" :  "--",
        viewable: true
    },
    review_category: {
        title: "Progcap Review",
        key: "review_category",
        dataIndex: "review_category",
        editable: false,
        viewable: true,
        fieldType: INPUT_TYPES.SELECT,
        render: (_, record) => record.deviation === "Yes" ? record.deviation_reason :  record.title ? "" : "OK"
    },
    remarks: {
        title: "Progcap Remarks",
        key: "remarks",
        dataIndex: "remarks",
        fieldType: INPUT_TYPES.TEXTAREA,
        editable: true,
    },
});

export const PORTFOLIO_COLUMNS = [
    {
      title: '',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Vintage',
      dataIndex: 'vintage',
      key: 'vintage',
    },
    {
      title: 'No of Customers',
      dataIndex: 'customers',
      key: 'customers',
    },
    {
      title: '% Active',
      dataIndex: 'activePercent',
      key: 'activePercent',
      render: _ => formatCurrency(_, false)
    },
    {
      title: 'PAR 0(Cr.)',
      dataIndex: 'par0',
      key: 'par0',
      render: _ => formatCurrency(_, false)
    },
    {
      title: 'PAR 30(Cr.)',
      dataIndex: 'par30',
      key: 'par30',
      render: _ => formatCurrency(_, false)
    },
    {
      title: 'PAR 90(Cr.)',
      dataIndex: 'par90',
      key: 'par90',
      render: _ => formatCurrency(_, false)
    },
    {
      title: 'Throughput(Cr.)',
      dataIndex: 'throughput',
      key: 'throughput',
      render: _ => formatCurrency(_, false)
    },
    {
      title: 'AUM(Cr.)',
      dataIndex: 'aum',
      key: 'aum',
      render: _ => formatCurrency(_, false)
    },
    {
      title: 'Profitability',
      dataIndex: 'profitability',
      key: 'profitability',
      render: _ => formatCurrency(_, false)
    },
  ];

export const ADHOC_CASES_COLS_PRE_APPROVED = new OrderedMap({
  
    case_id: { title: "Case ID", dataIndex: "case_id", key: "case_id", width: 80, align: "center" },
    buyer_gstin: {
        title: "Buyer GSTIN",
        dataIndex: "buyer_gstin",
        key: "buyer_gstin",
        align: "center",
        width: 120,
    },
    buyer_name: {
        title: "Buyer Name",
        dataIndex: "buyer_name",
        key: "buyer_name",
        width: 120,
        align: "center",
    },
    new_sanction_limit: {
        title: "Sanction Limit",
        dataIndex: "new_sanction_limit",
        key: "new_sanction_limit",
        width: 120,
        align: "center",
        render(text) {
            return {
                children:
          text && typeof text === "number" ? formatCurrency(text, false) : "--",
            };
        },
    },
    recommended_limit: {
        title: "Adhoc Recommended",
        dataIndex: "recommended_limit",
        key: "recommended_limit",
        width: 120,
        align: "center",
        render(text) {
            return {
                children:
          text && typeof text === "number" ? formatCurrency(text, false) : "--",
            };
        },
    },
    // utilization: {
    //     title: "Adhoc Proposed",
    //     dataIndex: "utilization",
    //     key: "utilization",
    //     width: 120,
    //     align: "center",
    //     render(text) {
    //         return {
    //             children:
    //       text && typeof text === "number" ? formatCurrency(text, false) : "--",
    //         };
    //     },
    // },
    buyer_status: {
        title: "Category",
        dataIndex: "category",
        key: "category",
        align: "center",
        width: 120,
    },
});
export const ADHOC_CASES_COLS_REST = new OrderedMap({
    action_detail_id: {
        title: "Action Detail Id",
        dataIndex: "action_detail_id",
        key: "action_detail_id",
        width: 100,
        align: "center",
        render: (_, item) => {
            return item.id || "--";
        },
    },
    case_id: {
        title: "Case ID",
        dataIndex: "case_id",
        key: "case_id",
        align: "center",
        width: 80,
        render: (_, item) => {
            return item.extra_data && item.extra_data.json_obj.case_id || "--";
        },
    },
    buyer_gstin: {
        title: "Buyer GSTIN",
        dataIndex: "buyer_gstin",
        key: "buyer_gstin",
        align: "center",
        width: 150,
        render: (_, item) =>
            item.extra_data && item.extra_data.json_obj.buyer_gstin || "--",
    },
    buyer_name: {
        title: "Buyer Name",
        dataIndex: "buyer_name",
        key: "buyer_name",
        width: 120,
        align: "center",
        render: (_, item) => item.extra_data && item.extra_data.json_obj.buyer_name || "--",
    },
    new_sanction_limit: {
        title: "Sanction Limit",
        dataIndex: "new_sanction_limit",
        key: "new_sanction_limit",
        width: 120,
        align: "center",
        render(_, item) {
            return {
                children:
          item &&
          item.extra_data &&
          item.extra_data?.json_obj?.new_sanction_limit &&
          typeof item.extra_data.json_obj.new_sanction_limit === "number"
              ? formatCurrency(item.extra_data.json_obj.new_sanction_limit, false)
              : "--",
            };
        },
    },
    recommended_limit: {
        title: "Adhoc Recommended",
        dataIndex: "recommended_limit",
        key: "recommended_limit",
        width: 120,
        align: "center",
        render(_, item) {
            return {
                children:
          item &&
          item.extra_data &&
          item.extra_data.json_obj &&
          typeof item.extra_data.json_obj.recommended_limit === "number"
              ? formatCurrency(item.extra_data.json_obj.recommended_limit, false)
              : "--",
            };
        },
    },
    utilization: {
        title: "Adhoc Requested (Sales)",
        dataIndex: "utilization",
        key: "utilization",
        width: 100,
        align: "center",
        render(_, item) {
            return {
                children:
          item &&
          item.extra_data &&
          item.extra_data.sales_adhoc_create_data &&
          item.extra_data.sales_adhoc_create_data[
            item?.extra_data?.sales_adhoc_create_data?.length - 1
          ].adhoc_limit_requested
              ? formatCurrency(
                  item.extra_data.sales_adhoc_create_data[
                  item?.extra_data?.sales_adhoc_create_data?.length - 1
                  ].adhoc_limit_requested,
                  false
              )
              : "--",
            };
        },
    },
    adhoc_requested_credit: {
        title: "Adhoc Requested (Credit)",
        dataIndex: "adhoc_requested_credit",
        key: "adhoc_requested_credit",
        width: 100,
        align: "center",
        render(_, item) {
            return {
                children:
          item &&
          item.extra_data &&
          item.extra_data.credit_adhoc_proceed_data &&
          Object.keys(item.extra_data.credit_adhoc_proceed_data).length >= 1
              ? formatCurrency(
                  item.extra_data.credit_adhoc_proceed_data.credit_approved_adhoc_limit,
                  false
              )
              : "--",
            };
        },
    },
    category: {
        title: "Category",
        dataIndex: "category",
        key: "category",
        width: 80,
        align: "center",
        render: (_, item) => item.extra_data && item.extra_data?.json_obj?.category || "--",
    },
    this_user: {
        title: "Current Stage",
        dataIndex: "current_case_task[0]",
        key: "this_user",
        width: 100,
        align: "center",
        render: (_ , item) => item && item.open_action_task && item.open_action_task.stage_name || "--",
    },
    current_user: {
        title: "Current User",
        dataIndex: "current_user",
        key: "current_user",
        align: "center",
        width: 100,
        render: (_ , item) => item && item.open_action_task && item.open_action_task.this_user_name || "--",
    },
   
});
export const ADHOC_PARAMETERS_COLS = new OrderedMap({
    s_no: {
        title: "S.No",
        dataIndex: "s_no",
        key: "s_no",
        className: "table-row-light",
        width: 25,
        render(text) {
            return {
                props: {
                    style: { background: "#D9D9D961",
                    }
                },
                children: text
            };
        }
    },
    parameter: {
        title: "Parameter",
        dataIndex: "parameter",
        key: "parameter",
        className: "table-row-light",
        width: 80,
        render(text) {
            return {
                props: {
                    style: { background: "#D9D9D961",
                    }
                },
                children: text
            };
        }
    },
    criteria: {
        title: "Criteria",
        dataIndex: "criteria",
        key: "criteria",
        className: "table-row-light",
        width: 80,
        render(text) {
            return {
                props: {
                    style: { background: "#D9D9D961",
                    }
                },
                children: text
            };
        }
    },
    value: {
        title: "Submitted Values",
        dataIndex: "value",
        key: "value",
        className: "table-row-light",
        width: 80,
        render(text) {
            return {
                props: {
                    style: { background: "#D9D9D961",
                    }
                },
                children: text
            };
        }
    },
    updated_value: {
        title: "Updated Values",
        dataIndex: "updated_value",
        key: "updated_value",
        className: "table-row-light",
        width: 80,
        render(text) {
            return {
                props: {
                    style: { background: "#D9D9D961",
                    }
                },
                children: text
            };
        }
    },
});


export const DB_DEVIATIONS_COLUMNS = (deviation_types , underlying_commitment_types) => new OrderedMap({
    deviation_type: {
        title: "Deviation Type",
        dataIndex: "deviation_type",
        key: "deviation_type",
        render: _ => deviation_types.filter(__ => __.value == _ )[0].label
    },
    commitment: {
        title: "Underlying commitment",
        dataIndex: "commitment",
        key: "commitment",
        width: 200,
        render: _ => underlying_commitment_types.filter(__ => __.value == _ )[0].label
    },
    ho_remarks: {
        title: "HO Remarks",
        key: "ho_remarks",
        dataIndex: "ho_remarks",
        width: 150,
        render: _ => (_ ? _ : "--")
    },
    deviation_reason: {
        title: "Deviation Reason",
        key: "deviation_reason",
        dataIndex: "deviation_reason",
    },
    key_approver: {
        title: "Key Approver",
        key: "key_approver",
        dataIndex: "key_approver",
        render: _ => _.full_name
    },
    request_raised_by: {
        title: "Request Raised By",
        key: "request_raised_by",
        dataIndex: "request_raised_by",
        render: _ => (_ ? _.full_name : "--")
    },
    requested_on: {
        title: "Requested On",
        key: "requested_on",
        dataIndex: "requested_on",
    },
    "action": {
        title: "Action",
        key: "action",
        dataIndex: "action"
    },
});

export const CASE_DB_DEVIATIONS_COLUMNS = (deviation_types) =>  new OrderedMap({
    deviation_type: {
        title: "Deviation Type",
        dataIndex: "deviation_type",
        key: "deviation_type",
        render: _ => deviation_types.filter(__ => __.value == _ )[0].label
    },
    deviation_reason: {
        title: "Deviation Reason",
        key: "deviation_reason",
        dataIndex: "deviation_reason",
    },
    
    create_date: {
        title: "Approved Date",
        key: "create_date",
        dataIndex: "create_date",
        render: _ => moment(_).format('YYYY-MM-DD')
    },
    key_approver: {
        title: "Approved By",
        key: "key_approver",
        dataIndex: "key_approver",
        render: _ => _.full_name
    },
    ho_remarks: {
        title: "Approved Remarks",
        key: "ho_remarks",
        dataIndex: "ho_remarks",
        width: 150,
        render: _ => (_ ? _ : "--")
    }
});


export const LIMIT_INFO_COLUMNS = new OrderedMap({
    type_2: {
        title: "",
        dataIndex: "type_2",
        key: "type_2",
        width: 120,
        render: _ => _.charAt(0).toUpperCase() + _.slice(1),
        align: "center",

    },
    proposed_limit: {
        title: "Proposed Limit",
        dataIndex: "proposed_limit",
        key: "proposed_limit",
        width: 120,
        render: _ => formatCurrency(_, false),
        align: "center",
    },
    sanction_limit: {
        title: "Sanction Limit",
        dataIndex: "sanction_limit",
        key: "sanction_limit",
        width: 120,
        render: _ => formatCurrency(_, false),
        align: "center",
    },
    normal_limit: {
        title: "Normal Limit",
        dataIndex: "normal_limit",
        key: "normal_limit",
        width: 120,
        render: _ => formatCurrency(_, false),
        align: "center",
    },
    current_limit: {
        title: "Current Limit",
        dataIndex: "current_limit",
        key: "current_limit",
        width: 120,
        render: _ => formatCurrency(_, false),
        align: "center",
    },   
    adhoc_limit: {
        title: "Adhoc Limit",
        dataIndex: "adhoc_limit",
        key: "adhoc_limit",
        width: 120,
        render: _ => formatCurrency(_, false),
        align: "center",
    },

});